import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion, AnimatePresence, useMotionValue, useTransform, useSpring } from "framer-motion";
import { FiActivity, FiDroplet, FiSun, FiMoon, FiCoffee, FiAward, FiCalendar, FiClock, FiHeart,
  FiShield, FiStar, FiTrendingUp, FiPlay, FiArrowRight,
  FiCheck, FiLock, FiUser, FiZap, FiXCircle,
  FiX, FiThumbsUp, FiDownload, FiList, FiFileText, FiCheckCircle, FiBookmark, FiTarget, FiRadio, FiVideo, FiEye,
  FiWind, FiPause } from "react-icons/fi";
import { GiLotus } from "react-icons/gi";
import UserDashboardLayout from "../../layouts/UserDashboardLayout";
import Topbar from "../../components/userDashboard/Topbar";
import BrandLoader from "../../components/BrandLoader";
import { userAPI, liveSessionAPI, practiceVideoAPI, achievementAPI, goalsAPI, dietPlanAPI } from "../../services/api";
import { isFeaturePermitted } from "../../utils/featureAccess";
import { useCheckIn } from "../../context/CheckInContext";
import { useDynamicPackages } from "../../hooks/useDynamicPackages";
import { HubConnectionBuilder } from "@microsoft/signalr";

/* ─── SHARED GLASS STYLE ─────────────────────────────────────────────── */
const glass = {
  background: "rgba(255,255,255,0.78)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,1)",
};

const T = {
  orange: "#F29A0C",
  orange2: "#E97818",
  olive: "#6B8A5A",
  olive2: "#8BA47A"
};

const TIERS = {
  free:     { label:"Free Trial", rank:0, color:"#888", bg:"rgba(180,180,180,0.12)", border:"rgba(180,180,180,0.25)", grad:"linear-gradient(135deg,#AAA,#888)" },
  basic:    { label:"Basic",      rank:1, color:"#6B8A5A", bg:"rgba(107,138,90,0.12)", border:"rgba(107,138,90,0.28)", grad:"linear-gradient(135deg,#6B8A5A,#8BA47A)" },
  standard: { label:"Standard",   rank:2, color:"#F29A0C", bg:"rgba(242,154,12,0.12)", border:"rgba(242,154,12,0.28)", grad:"linear-gradient(135deg,#F29A0C,#E97818)" },
  premium:  { label:"Premium",    rank:3, color:"#C8660A", bg:"rgba(242,154,12,0.18)", border:"rgba(242,154,12,0.4)", grad:"linear-gradient(135deg,#F29A0C,#C8660A,#E97818)" },
  pro:      { label:"Pro",        rank:4, color:"#27500A", bg:"rgba(78,104,69,0.18)", border:"rgba(78,104,69,0.4)", grad:"linear-gradient(135deg,#4E6845,#6B8A5A,#27500A)" },
};

const normalizeTier = (t) => {
  if (!t) return "free";
  const low = t.toLowerCase().trim();
  if (low.includes("pro") || low.includes("ytt")) return "pro";
  if (low.includes("premium") || low.includes("premiun")) return "premium";
  if (low.includes("standard")) return "standard";
  if (low.includes("basic")) return "basic";
  return low;
};




export const getTierRank = (tier, packages = []) => {
  if (!tier) return 0;
  const low = String(tier).trim().toLowerCase();
  if (low === "free" || low === "free trial" || low === "trial" || low === "all") return 0;
  if (low === "basic" || low === "basic plan") return 1;
  if (low === "standard" || low === "standard plan") return 2;
  if (low === "premium" || low === "premium plan") return 3;
  if (low === "pro" || low === "pro plan" || low === "ytt" || low === "ytt batch") return 4;

  const pkg = packages.find(p => 
    (p.packageType && p.packageType.trim().toLowerCase() === low) || 
    (p.name && p.name.trim().toLowerCase() === low)
  );
  if (pkg) return pkg.accessLevel ?? 1;

  if (low.includes("pro") || low.includes("ytt") || low.includes("vip")) return 4;
  if (low.includes("premium") || low.includes("gold")) return 3;
  if (low.includes("standard") || low.includes("silver")) return 2;
  if (low.includes("basic") || low.includes("bronze")) return 1;
  return 1;
};

export const canUserAccessContent = (contentTier, userTier, userRank = 0, packages = []) => {
  if (!contentTier) return true;
  const cTier = String(contentTier).toLowerCase().trim();
  if (cTier === "free" || cTier === "free trial" || cTier === "trial" || cTier === "all" || cTier === "all users") return true;

  const uTier = String(userTier || "").toLowerCase().trim();

  // 1. Direct tier match
  if (uTier && (uTier === cTier || uTier.includes(cTier) || cTier.includes(uTier))) {
    return true;
  }

  const requiredRank = getTierRank(contentTier, packages);

  // 2. Rank comparison (User rank >= required content rank grants access)
  return userRank >= requiredRank;
};

// ─── UPGRADE MODAL ────────────────────────────────────────────────────────────
function UpgradeModal({ session, onClose, packages = [] }) {
  const navigate = useNavigate();
  const tierName = session?.tier || "Standard";

  const matchedPkg = useMemo(() => {
    if (!packages || packages.length === 0) return null;
    const low = String(tierName).toLowerCase().trim();
    return packages.find(p => {
      const pName = (p.name || "").toLowerCase().trim();
      const pType = (p.packageType || "").toLowerCase().trim();
      return pName === low || pType === low || pName.includes(low) || low.includes(pName);
    });
  }, [packages, tierName]);

  const tierLabel = matchedPkg?.name || session?.tier || "Membership";

  const adminDescription = matchedPkg?.description || 
    `"${session?.title || 'This session'}" is available exclusively for ${tierLabel} members and above. Upgrade your plan to get full live streaming and recorded class access.`;

  const perks = useMemo(() => {
    if (matchedPkg?.featuresJson) {
      try {
        const parsed = JSON.parse(matchedPkg.featuresJson);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.filter(Boolean);
        }
      } catch (e) {
        if (typeof matchedPkg.featuresJson === "string") {
          return matchedPkg.featuresJson.split(/\r?\n|,/).map(s => s.trim()).filter(Boolean);
        }
      }
    }
    return [
      `Full access to all ${tierLabel} live interactive classes`,
      "Priority booking and seat reservations before general enrollment",
      session?.recorded ? "Full HD recorded session replay available anytime" : "Interactive real-time audio & video access",
      "Direct instructor Q&A and personalized posture feedback",
      "Access to complete yoga curriculum and practice guides"
    ];
  }, [matchedPkg, tierLabel, session?.recorded]);

  const priceText = matchedPkg?.discountedPrice 
    ? ` (₹${matchedPkg.discountedPrice.toLocaleString()})`
    : matchedPkg?.price 
      ? ` (₹${matchedPkg.price.toLocaleString()})`
      : "";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(14px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.85, y: 28 }} animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.85, y: 28 }} transition={{ type: "spring", stiffness: 320, damping: 30 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md rounded-[32px] overflow-hidden"
        style={{ boxShadow: "0 40px 100px rgba(0,0,0,0.45)", background: "#fff" }}>

        {/* Header gradient */}
        <div className="relative p-8 text-white"
          style={{ background: "linear-gradient(145deg,#F29A0C,#E97818)" }}>
          <div className="absolute inset-0 opacity-[0.07]"
            style={{ backgroundImage: "radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize: "18px 18px" }} />
          <button onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 rounded-full flex items-center justify-center border-none cursor-pointer"
            style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}>
            <FiX size={15} />
          </button>
          <div className="w-14 h-14 rounded-[18px] flex items-center justify-center mb-5"
            style={{ background: "rgba(255,255,255,0.2)", backdropFilter: "blur(10px)" }}>
            <FiLock size={22} />
          </div>
          <h2 className="font-black text-[22px] mb-2" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
            {tierLabel} Plan Required
          </h2>
          <p className="text-white/90 text-[13px] leading-[1.7]">
            {adminDescription}
          </p>
        </div>

        {/* Body */}
        <div style={{ background: "#fff", padding: "24px 28px" }}>
          <p className="text-[12px] font-bold uppercase tracking-[1px] mb-4 text-zinc-400">
            What you unlock with {tierLabel}
          </p>
          <div className="space-y-3 mb-6">
            {perks.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)" }}>
                  <FiCheck size={10} color="#fff" />
                </div>
                <span className="text-[13px] text-zinc-700 font-medium">{item}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-3 mt-6">
            <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
              onClick={() => {
                onClose();
                navigate("/user/settings?tab=subscription");
              }}
              className="flex-1 h-12 rounded-full font-bold text-[14px] text-white flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)",
                boxShadow: "0 10px 28px rgba(242,154,12,0.38)", border: "none", cursor: "pointer",
                fontFamily: "'Inter',sans-serif" }}>
              Upgrade to {tierLabel}{priceText} <FiArrowRight style={{ display: "inline", marginLeft: 6 }} />
            </motion.button>
            <button onClick={onClose}
              className="h-12 px-5 rounded-full font-semibold text-[13px]"
              style={{ background: "rgba(200,200,200,0.12)", border: "1px solid rgba(239,229,216,0.8)",
                color: "#999", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              Later
            </button>
          </div>

          <p className="text-center text-[11px] mt-4 text-zinc-400">
            Cancel anytime · Instant access · All plans include full member benefits
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

function TierBadge({ tier }) {
  const tKey = normalizeTier(tier);
  const t = TIERS[tier] || TIERS[tKey] || { label: tier || "Free Trial", color: T.orange, bg: "rgba(242,154,12,0.12)", border: "rgba(242,154,12,0.28)" };
  const displayLabel = tier || t.label || "Free Trial";
  return (
    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full border"
      style={{ background:t.bg, borderColor:t.border }}>
      <FiShield style={{ color:t.color, fontSize:12 }}/>
      <span style={{ fontSize:11, fontWeight:800, color:t.color }}>{displayLabel}</span>
    </div>
  );
}

function PlayerModal({ video, progressPercent, onVideoUpdate, onClose }) {
  const [tab, setTab]           = useState("chapters");
  const [progress, setProgress] = useState(progressPercent || 0);

  const [views, setViews] = useState(video.views || 0);
  const [rating, setRating] = useState(video.averageRating || video.rating || 0);
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  const [note, setNote] = useState("");
  const [noteSavedMessage, setNoteSavedMessage] = useState(false);

  const videoRef = useRef(null);
  const lastProgressRef = useRef(progressPercent || 0);

  useEffect(() => {
    const updateViews = async () => {
      try {
        const res = await practiceVideoAPI.interactVideo(video.id, true);
        if (res.data?.success) {
          setViews(res.data.views);
          setRating(res.data.rating);
          if (onVideoUpdate) {
            onVideoUpdate(video.id, { views: res.data.views, rating: res.data.rating });
          }
        }
      } catch (err) {
        console.error("Failed to increment views:", err);
      }
    };
    updateViews();
  }, [video.id]);

  useEffect(() => {
    const savedNote = localStorage.getItem(`divyayoga_video_note_${video.id}`) || "";
    setNote(savedNote);
  }, [video.id]);

  const handleRate = async (rateVal) => {
    setUserRating(rateVal);
    try {
      const res = await practiceVideoAPI.interactVideo(video.id, false, rateVal);
      if (res.data?.success) {
        setRating(res.data.rating);
        if (onVideoUpdate) {
          onVideoUpdate(video.id, { rating: res.data.rating });
        }
      }
    } catch (err) {
      console.error("Failed to submit rating:", err);
    }
  };

  const handleSaveNote = () => {
    localStorage.setItem(`divyayoga_video_note_${video.id}`, note);
    setNoteSavedMessage(true);
    setTimeout(() => setNoteSavedMessage(false), 2000);
  };

  const handleTimeUpdate = (e) => {
    const videoEl = e.currentTarget;
    if (videoEl.duration) {
      const pct = Math.round((videoEl.currentTime / videoEl.duration) * 100);
      // Throttle updates to prevent continuous parent re-renders and video stuttering
      if (pct >= lastProgressRef.current + 5 || pct === 100) {
        lastProgressRef.current = pct;
        setProgress(pct);
        if (onVideoUpdate) {
          onVideoUpdate(video.id, { pct });
        }
      }
    }
  };

  const handleEnded = () => {
    setProgress(100);
    if (onVideoUpdate) {
      onVideoUpdate(video.id, { pct: 100 });
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current && progressPercent > 0 && progressPercent < 100) {
      const startTime = (progressPercent / 100) * videoRef.current.duration;
      videoRef.current.currentTime = startTime;
    }
  };

  const totalMins = video.durationInMinutes || 30;
  const totalSecs = totalMins * 60;

  const formatSecs = (s) => {
    const min = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${min}:${String(sec).padStart(2, "0")}`;
  };

  const chapters = useMemo(() => {
    const rawChapters = video.chapters || video.Chapters;
    if (Array.isArray(rawChapters) && rawChapters.length > 0) {
      return rawChapters.map((ch, i) => {
        const startSec = ch.startTime ?? ch.start ?? (i * (totalSecs / rawChapters.length));
        const durSec = ch.duration ?? ch.dur ?? (totalSecs / rawChapters.length);
        return {
          num: i + 1,
          title: ch.title || ch.name || `Part ${i + 1}`,
          startTime: startSec,
          dur: formatSecs(durSec),
          startStr: formatSecs(startSec),
          done: progress >= ((startSec + durSec) / totalSecs) * 100
        };
      });
    }
    return [
      {
        num: 1,
        title: video.title || "Full Practice Session",
        startTime: 0,
        dur: formatSecs(totalSecs),
        startStr: "0:00",
        done: progress >= 95
      }
    ];
  }, [video, totalSecs, progress]);

  const handleChapterClick = (startTime) => {
    if (videoRef.current) {
      videoRef.current.currentTime = startTime;
      videoRef.current.play();
    }
  };

  const descText = useMemo(() => {
    if (!video.description) return "No description provided.";
    if (video.description.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(video.description);
        return parsed.desc || parsed.Description || "No description provided.";
      } catch (e) {
        // Fallback
      }
    }
    return video.description;
  }, [video.description]);

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-6"
      style={{ background:"rgba(0,0,0,0.72)", backdropFilter:"blur(18px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.88, y:28 }} animate={{ scale:1, y:0 }} exit={{ scale:0.88, y:28 }}
        transition={{ type:"spring", stiffness:300, damping:28 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-[1060px] rounded-[32px] overflow-hidden flex flex-col"
        style={{ background:"rgba(255,255,255,0.97)", boxShadow:"0 40px 120px rgba(0,0,0,0.45)" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-150">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 rounded-full bg-gradient-to-b from-[#F29A0C] to-[#E97818]" />
            <div>
              <p className="text-[10px] font-extrabold text-[#F29A0C] uppercase tracking-[1.2px]">Now Playing</p>
              <p className="text-[18px] font-black text-[#1F1F1F] tracking-[-0.3px]" style={{ fontFamily:"'Poppins',sans-serif" }}>{video.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <TierBadge tier={video.tier}/>
            <motion.button whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }} onClick={onClose}
              className="w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center cursor-pointer border-none">
              <FiX className="text-[17px] text-[#555]" />
            </motion.button>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_320px]">
          {/* Player screen */}
          <div>
            <div className="h-[340px] relative bg-black flex items-center justify-center">
              <video 
                ref={videoRef}
                src={video.videoUrl} 
                controls 
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                autoPlay 
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleEnded}
                onLoadedMetadata={handleLoadedMetadata}
                className="w-full h-full object-contain"
              />
            </div>

            {/* Video meta */}
            <div className="p-6 border-b border-zinc-150">
              <p className="text-[13px] text-[#666] leading-relaxed mb-4">{descText}</p>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F29A0C] to-[#E97818] flex items-center justify-center text-white text-[12px] font-black" style={{ fontFamily:"'Poppins',sans-serif" }}>
                    YT
                  </div>
                  <div>
                    <p className="text-[12px] font-bold text-[#1F1F1F]">Yoga Teacher</p>
                    <p className="text-[10px] text-[#AAA]">Lead Instructor</p>
                  </div>
                </div>
                
                {/* Dynamic Rating Star Interaction component */}
                <div className="flex gap-2 ml-auto items-center">
                  <div className="flex items-center gap-0.5 mr-2">
                    {[1,2,3,4,5].map(starNum => {
                      const isLit = starNum <= (hoverRating || userRating || Math.round(rating));
                      return (
                        <FiStar 
                          key={starNum}
                          style={{ 
                            color: isLit ? T.orange : "#CCC", 
                            fontSize:15, 
                            fill: isLit ? T.orange : "none",
                            cursor:"pointer",
                            transition:"all 0.15s"
                          }} 
                          onMouseEnter={() => setHoverRating(starNum)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() => handleRate(starNum)}
                        />
                      );
                    })}
                    <span className="text-[11px] font-extrabold text-[#F29A0C] ml-1" style={{ fontFamily:"'Inter',sans-serif" }}>
                      {rating.toFixed(1)} ({views} views)
                    </span>
                  </div>

                  <motion.button whileHover={{ scale:1.04 }} whileTap={{ scale:0.95 }}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full border-none cursor-pointer text-[11px] font-bold"
                    style={{ background:"rgba(242,154,12,0.1)", color:"#C8660A" }}>
                    <FiThumbsUp size={11}/> Helpful
                  </motion.button>
                  <motion.button whileHover={{ scale:1.04 }} whileTap={{ scale:0.95 }}
                    onClick={() => {
                      practiceVideoAPI.toggleBookmark(video.id).catch(() => null);
                      toast.success("Video bookmarked to library!");
                    }}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full border-none cursor-pointer text-[11px] font-bold"
                    style={{ background:"rgba(242,154,12,0.1)", color:"#C8660A" }}>
                    <FiBookmark size={11}/> Bookmark
                  </motion.button>
                </div>
              </div>
            </div>
          </div>

          {/* Side panel */}
          <div className="border-l border-zinc-150 h-[480px] overflow-y-auto">
            <div className="flex border-b border-zinc-150">
              {["chapters","notes"].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className="flex-1 py-3.5 bg-transparent border-none cursor-pointer text-[12px] font-bold flex items-center justify-center gap-1.5"
                  style={{
                    color:tab===t?T.orange:"#CCC",
                    borderBottom:tab===t?`2px solid ${T.orange}`:"2px solid transparent"
                  }}>
                  {t==="chapters" ? <FiList size={13}/> : <FiFileText size={13}/>}
                  {t}
                </button>
              ))}
            </div>
            
            {/* Dynamic seekable chapters list */}
            {tab==="chapters" && (
              <div className="p-2.5">
                <p className="text-[10px] text-[#AAA] px-2.5 py-1 m-0">Tap a chapter to skip to timestamp</p>
                {chapters.map(ch => (
                  <motion.div key={ch.num} whileHover={{ x:5, background:"rgba(242,154,12,0.05)" }}
                    onClick={() => handleChapterClick(ch.startTime)}
                    className="flex items-center gap-2.5 p-2.5 rounded-xl cursor-pointer transition-all duration-200">
                    <div className="w-5.5 h-5.5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background:ch.done?`linear-gradient(135deg,${T.olive},${T.olive2})`:"rgba(200,200,200,0.18)" }}>
                      {ch.done ? <FiCheck className="text-white text-[10px]"/> : <span className="text-[9px] font-extrabold text-[#CCC]">{ch.num}</span>}
                    </div>
                    <p className="flex-1 text-[12px] font-semibold text-[#1F1F1F] truncate m-0">{ch.title}</p>
                    <span className="text-[10px] text-[#F29A0C] font-bold flex-shrink-0">{ch.startStr}</span>
                  </motion.div>
                ))}
              </div>
            )}
            
            {/* Functional timestamped notes persistence */}
            {tab==="notes" && (
              <div className="p-4">
                <p className="text-[11px] text-[#AAA] mb-2.5">Personal practice notes</p>
                <textarea rows={7} placeholder={`Notes for "${video.title}"…\ne.g. 12:34 · alignment checkpoint`}
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  className="w-full rounded-[14px] p-3 text-[12px] border border-zinc-200 outline-none leading-relaxed resize-none"
                  style={{ background:"rgba(255,248,238,0.7)", color:"#333" }}/>
                
                {noteSavedMessage && (
                  <p className="text-[11px] text-[#6B8A5A] font-extrabold mt-1.5 text-center">✓ Notes saved successfully!</p>
                )}

                <motion.button whileHover={{ y:-1 }} whileTap={{ scale:0.97 }}
                  onClick={handleSaveNote}
                  className="mt-2.5 w-full h-[38px] rounded-full border-none cursor-pointer text-white text-[12px] font-extrabold"
                  style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})` }}>
                  Save Note
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── 3D TILT CARD ─────────────────────────────────────────────────── */
function TiltCard({ children, className = "", style = {}, intensity = 8 }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [intensity, -intensity]), { stiffness: 260, damping: 28 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-intensity, intensity]), { stiffness: 260, damping: 28 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left) / r.width - 0.5);
    y.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <motion.div
      ref={ref} onMouseMove={onMove}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d", perspective: 900, ...style }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── ANIMATED COUNT-UP ─────────────────────────────────────────────── */
function AnimCount({ to, duration = 1200 }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const pct = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(pct * to));
      if (pct < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [to, duration]);
  return <span>{val}</span>;
}

/* ─── FLOATING PARTICLES ─────────────────────────────────────────────── */
function Particles() {
  const pts = Array.from({ length: 20 }, (_, i) => ({
    id: i, x: Math.random() * 100, y: Math.random() * 100,
    s: Math.random() * 5 + 2, d: Math.random() * 10 + 6, delay: Math.random() * 5,
    color: i % 3 === 0 ? "rgba(242,154,12,0.4)" : i % 3 === 1 ? "rgba(107,138,90,0.3)" : "rgba(233,120,24,0.25)",
  }));
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {pts.map(p => (
        <motion.div key={p.id} className="absolute rounded-full"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.s, height: p.s, background: p.color }}
          animate={{ y: [-25, 25, -25], x: [-12, 12, -12], opacity: [0.2, 0.8, 0.2] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.delay, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

/* ─── COUNTDOWN TICKER & HELPERS ───────────────────────────────────── */
function formatCountdown(targetDate) {
  const diff = Math.max(0, targetDate - Date.now());
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0)  return `${h}h ${m}m`;
  return `${m}m ${s}s`;
}

function Countdown({ targetDate, style = {} }) {
  const [tick, setTick] = useState(formatCountdown(targetDate));
  useEffect(() => {
    const t = setInterval(() => setTick(formatCountdown(targetDate)), 1000);
    return () => clearInterval(t);
  }, [targetDate]);
  return <span style={style}>{tick}</span>;
}

/* ─── HERO SECTION ─────────────────────────────────────────────────── */
function HeroSection({
  profile,
  liveProgress,
  sessions = [],
  daysLeft,
  videos = [],
  videoProgressMap = {},
  attendanceHistory = [],
  onContinuePractice,
  onExploreClasses,
  onExploreMeditation,
  onManageMembership,
  onBookSession,
  onCancelBooking,
  onJoinSession,
  onUpgradeSession,
  userTier = "Free",
  userRank = 0,
  packages = []
}) {
  const fullName = profile?.fullName || "User";
  const [greeting, setGreeting] = useState("Good Morning");

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 17) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  }, []);

  const { stats, checkedIn, checkIn } = useCheckIn();
  const completedCount = stats?.totalPractices || attendanceHistory?.length || 0;
  const streakDays = stats?.streakDays || 0;
  const bestStreak = stats?.bestStreak || streakDays;
  const wellnessScore = stats.wellnessScore;

  const completedVideosCount = useMemo(() => {
    if (!videos || !Array.isArray(videos) || videos.length === 0) return 0;
    return videos.filter(v => {
      const vidKey = (v.id || v.Id || "").toString();
      const val = videoProgressMap ? videoProgressMap[vidKey] : 0;
      const pct = typeof val === "number" ? val : (val?.progress ?? val?.Progress ?? 0);
      return pct >= 90 || pct === 100;
    }).length;
  }, [videos, videoProgressMap]);

  const activeSessions = useMemo(() => {
    if (!sessions || !Array.isArray(sessions)) return [];
    return sessions.filter(s => s && (s.isLive || s.isUpcoming));
  }, [sessions]);

  const singleSession = activeSessions.length === 1 ? activeSessions[0] : null;

  return (
    <section className="relative grid grid-cols-1 xl:grid-cols-[1fr_360px] 2xl:grid-cols-[1fr_420px] gap-5 mb-5">
      <motion.div animate={{ y: [0,-28,0], x:[0,18,0] }} transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-20 right-10 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle,rgba(242,154,12,0.16),transparent 70%)", filter: "blur(70px)" }} />
      <motion.div animate={{ y:[0,22,0], x:[0,-14,0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-0 left-0 w-72 h-72 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle,rgba(107,138,90,0.12),transparent 70%)", filter: "blur(60px)" }} />

      {/* Left Hero Main Card */}
      <motion.div initial={{ opacity:0, y:28 }} animate={{ opacity:1, y:0 }} transition={{ duration: 0.85 }}
        className="relative overflow-hidden rounded-[28px] sm:rounded-[38px] p-5 sm:p-8 md:p-10 flex flex-col justify-between" style={glass}>
        <div className="absolute inset-0 rounded-[inherit] pointer-events-none opacity-[0.03]"
          style={{ backgroundImage: "radial-gradient(circle,#000 1px,transparent 0)", backgroundSize: "24px 24px" }} />

        <div>
          {/* Top Header Row: Greeting & Badges on Left, Single Live/Upcoming Session Spotlight on Right */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
            <div className="flex-1 max-w-[540px]">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[11px] font-black text-amber-700 bg-amber-50 border border-amber-200 shadow-sm uppercase tracking-wider">
                  <FiSun size={13} className="text-amber-500"/> Wellness Journey Active
                </span>
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[11px] font-black text-emerald-800 bg-emerald-50 border border-emerald-200 shadow-sm uppercase tracking-wider">
                  <GiLotus size={14} className="text-emerald-600"/> {(profile?.tier || profile?.activeSubscription?.packageName || profile?.subscriptions?.find(s => s.status === "Active")?.packageName || "Free Trial")} Yogi
                </span>
              </div>

              <h1 className="text-[32px] sm:text-[40px] md:text-[46px] xl:text-[54px] break-words font-black leading-[1.08] md:leading-[1.04] text-[#1F1F1F] mb-4"
                style={{fontFamily: "'Poppins', sans-serif",letterSpacing: "-2px"}}>
                {greeting},
                <br />
                <span
                  style={{
                    background: "linear-gradient(135deg,#F29A0C,#E97818)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent"
                  }}
                >
                  {fullName} ✦
                </span>
              </h1>

              <p className="text-[15px] text-[#999] leading-[1.8] max-w-[500px]">
                Stay consistent, stay mindful. Your wellness journey is progressing beautifully with{" "}
                <strong className="text-[#E97818]">{completedCount} practice sessions logged</strong>.
              </p>
            </div>

            {/* ── TOP RIGHT (INSIDE HERO CARD): SINGLE LIVE / UPCOMING SESSION SPOTLIGHT CARD ── */}
            {singleSession && (
              <div className="w-full lg:w-[320px] xl:w-[340px] flex-shrink-0">
                <TiltCard intensity={5}>
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
                    className="rounded-[28px] overflow-hidden p-5 text-white relative flex flex-col justify-between"
                    style={{
                      background: singleSession.isLive ? "linear-gradient(145deg,#ef4444,#dc2626)" : "linear-gradient(145deg,#F29A0C,#E97818)",
                      boxShadow: singleSession.isLive ? "0 18px 45px rgba(239,68,68,0.38)" : "0 18px 45px rgba(242,154,12,0.32)",
                      border: "1.5px solid rgba(255,255,255,0.25)"
                    }}>
                    <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
                      style={{ backgroundImage: "radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize: "16px 16px" }} />
                    <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-15 pointer-events-none"
                      style={{ background: "radial-gradient(circle,#fff,transparent)", filter: "blur(24px)", transform: "translate(30%,-30%)" }} />

                    <div className="relative">
                      <div className="flex items-center justify-between mb-2.5 gap-2 flex-wrap">
                        <motion.div animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.2, repeat: Infinity }}
                          className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-wider bg-black/20 px-2.5 py-1 rounded-full backdrop-blur-md">
                          <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                          {singleSession.isLive ? "Live Now" : "Upcoming Session"}
                        </motion.div>

                        {!singleSession.isLive && singleSession.scheduledAt && (
                          <span className="text-[10.5px] font-extrabold bg-white/20 backdrop-blur-md px-2.5 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                            <FiClock size={10.5} /> Starts in <Countdown targetDate={parseUtcDate(singleSession.scheduledAt)} style={{ fontWeight: 800 }} />
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/20 text-white backdrop-blur-sm">
                          {singleSession.category || "Yoga Class"}
                        </span>
                        {singleSession.tier && singleSession.tier.toLowerCase() !== "free" && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/25 text-white backdrop-blur-sm flex items-center gap-1">
                            <FiLock size={8} /> {singleSession.tier}
                          </span>
                        )}
                      </div>

                      <h3 className="font-black text-[17px] mb-1 line-clamp-1" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
                        {singleSession.title}
                      </h3>
                      <p className="text-white/80 text-[11.5px] mb-3 font-medium">
                        {singleSession.instructorName || singleSession.instructor || "Instructor TBA"} · {singleSession.durationInMinutes || 60} min
                      </p>

                      <div className="grid grid-cols-3 gap-1.5 mb-3">
                        {[
                          { label: "Enrolled", val: singleSession.enrolledCount ?? singleSession.enrolled ?? 0 },
                          { label: "Capacity", val: singleSession.maxCapacity ?? singleSession.capacity ?? 30 },
                          { label: "Duration", val: `${singleSession.durationInMinutes || 60}m` }
                        ].map((item) => (
                          <div key={item.label} className="rounded-[10px] p-1.5 text-center"
                            style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.15)" }}>
                            <div className="font-black text-[14px]" style={{ fontFamily: "'Poppins',sans-serif" }}>
                              {item.val}
                            </div>
                            <div className="text-[8px] text-white/75 font-bold uppercase tracking-wide mt-0.5">{item.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="relative pt-1">
                      {!canUserAccessContent(singleSession.tier, userTier, userRank, packages) ? (
                        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                          onClick={() => onUpgradeSession && onUpgradeSession(singleSession)}
                          className="w-full h-9 rounded-full font-black text-[11.5px] flex items-center justify-center gap-1.5 cursor-pointer shadow-md transition-transform"
                          style={{ background: "#fff", color: "#E97818", border: "none", fontFamily: "'Inter',sans-serif" }}>
                          <FiLock size={12} /> Unlock with {singleSession.tier || "Membership"}
                        </motion.button>
                      ) : singleSession.isLive ? (
                        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                          onClick={() => onJoinSession && onJoinSession(singleSession.id)}
                          className="w-full h-9 rounded-full font-black text-[12px] flex items-center justify-center gap-1.5 cursor-pointer shadow-lg transition-transform"
                          style={{ background: "rgba(255,255,255,0.95)", color: "#dc2626", border: "none", fontFamily: "'Inter',sans-serif" }}>
                          <FiRadio size={13} /> Enter Live Studio
                        </motion.button>
                      ) : singleSession.isCheckedIn ? (
                        <div className="flex gap-2">
                          <div className="flex-1 h-9 rounded-full font-black text-[11.5px] flex items-center justify-center gap-1.5 bg-white/20 border border-white/30 text-white shadow-sm">
                            <FiCheck size={13} /> Slot Booked
                          </div>
                          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                            onClick={() => onCancelBooking && onCancelBooking(singleSession.id)}
                            className="px-3 h-9 rounded-full font-bold text-[10.5px] flex items-center justify-center gap-1 bg-red-500/20 hover:bg-red-500/30 border border-red-200/30 text-white cursor-pointer transition-all">
                            <FiXCircle size={12} /> Cancel
                          </motion.button>
                        </div>
                      ) : (
                        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                          onClick={() => onBookSession && onBookSession(singleSession.id)}
                          className="w-full h-9 rounded-full font-black text-[11.5px] flex items-center justify-center gap-1.5 cursor-pointer shadow-md transition-all"
                          style={{ background: "#fff", color: "#E97818", border: "none", fontFamily: "'Inter',sans-serif" }}>
                          <FiCalendar size={12} /> Book Slot Now
                        </motion.button>
                      )}
                    </div>
                  </motion.div>
                </TiltCard>
              </div>
            )}
          </div>

          <div className="ud-kpi-cards grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            {[
              { icon: FiActivity, val: wellnessScore,  label: "Wellness Score", accent: "#F29A0C", sub: "/100" },
              { icon: FiZap,      val: streakDays,     label: bestStreak > 0 ? `Streak (Best: ${bestStreak}d)` : "Day Streak", accent: "#E97818", sub: "" },
              { icon: FiCalendar, val: completedCount, label: "Sessions Done",   accent: "#F29A0C", sub: "Sessions" },
              { icon: FiShield,   val: daysLeft,       label: "Days Left",       accent: "#6B8A5A", sub: "Days" },
              { icon: FiAward,    val: completedVideosCount, label: "Practice Done", accent: "#6B8A5A", sub: "Videos" },
            ].map((m, i) => (
              <motion.div key={m.label}
                initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay: 0.4 + i*0.08 }}
                whileHover={{ y: -6, scale: 1.02 }}
                className="rounded-[24px] p-4 cursor-default transition-all duration-300 shadow-md border border-white/80 hover:shadow-xl hover:-translate-y-1"
                style={{ background: "rgba(255,255,255,0.85)", border: `1px solid ${m.accent}1A`, boxShadow: "0 6px 20px rgba(0,0,0,0.04)" }}>
                <m.icon style={{ color: m.accent, fontSize: 18 }} />
                <p className="mt-2.5 text-[24px] font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.5px" }}>
                  <AnimCount to={m.val} duration={900 + i * 100} />
                  {m.sub && <span className="text-[12px] text-[#AAA] font-normal">{m.sub}</span>}
                </p>
                <p className="text-[10px] text-[#AAA] mt-0.5 whitespace-nowrap">{m.label}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <motion.button whileHover={{ y:-3, scale:1.02 }} whileTap={{ scale:0.98 }}
            onClick={onContinuePractice}
            className="h-[52px] px-8 rounded-full text-white font-bold text-sm flex items-center gap-2.5 cursor-pointer"
            style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", boxShadow: "0 16px 40px rgba(242,154,12,0.38)", fontFamily:"'Inter',sans-serif" }}>
            <FiPlay size={14} /> Continue Practice
          </motion.button>
          <motion.button whileHover={{ y:-3 }} whileTap={{ scale:0.98 }}
            onClick={onExploreClasses}
            className="h-[52px] px-7 rounded-full text-[#555] font-semibold text-sm cursor-pointer"
            style={{ background: "rgba(255,255,255,0.9)", border: "1px solid rgba(239,229,216,0.8)", fontFamily:"'Inter',sans-serif" }}>
            Explore Classes
          </motion.button>
        </div>
      </motion.div>

      {/* Right Column: Daily Wisdom + Checked In Today */}
      {(() => {
        const streakDays = stats?.streakDays || 0;
        const firstName = profile?.fullName ? profile.fullName.split(' ')[0] : "Yogi";

        const wisdom = (() => {
          if (checkedIn) {
            return {
              tag: "Today Checked-In",
              quote: `Namaste ${firstName}! Your daily practice is complete. Consistency builds deep mental clarity and physical balance.`,
              intention: "Cultivate gratitude for your dedicated practice today.",
              icon: FiCheckCircle
            };
          }
          if (streakDays >= 3) {
            return {
              tag: `${streakDays}-Day Streak`,
              quote: `Unstoppable momentum! You have built a ${streakDays}-day practice streak. Discipline transforms effort into joy.`,
              intention: "Protect your streak by checking in for today's session.",
              icon: FiZap
            };
          }
          if (completedCount >= 5) {
            return {
              tag: "Milestone Achieved",
              quote: `You have completed ${completedCount} live yoga sessions! Each class aligns your body, mind, and breath.`,
              intention: "Keep expanding your practice with today's live class.",
              icon: FiAward
            };
          }
          return {
            tag: "Daily Wisdom",
            quote: "The body benefits from movement, and the mind awakens in stillness. Begin today with a single mindful breath.",
            intention: "Complete your daily check-in to start tracking today.",
            icon: FiSun
          };
        })();

        const WisdomIcon = wisdom.icon;

        return (
          <div className="flex flex-col gap-3.5 justify-between">
            {/* ── ROW 1: DAILY WISDOM CARD ── */}
            <TiltCard className="flex-1">
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.85, delay: 0.1 }}
                className="relative overflow-hidden rounded-[34px] p-7 text-white flex flex-col justify-between h-full"
                style={{
                  background: "linear-gradient(135deg, #24321E, #36482D, #1A2515)",
                  border: "1.5px solid rgba(242, 154, 12, 0.45)",
                  boxShadow: "0 22px 55px rgba(36, 50, 30, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                }}>
                <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
                  style={{ backgroundImage: "radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize: "18px 18px" }} />
                <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full pointer-events-none"
                  style={{ background: "radial-gradient(circle, rgba(242,154,12,0.4), transparent 70%)", filter: "blur(38px)" }} />
                <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full pointer-events-none"
                  style={{ background: "radial-gradient(circle, rgba(107,138,90,0.35), transparent 70%)", filter: "blur(38px)" }} />

                <div>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      <motion.div animate={{ rotate: [0, 8, -8, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                        className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-lg"
                        style={{
                          background: "linear-gradient(135deg, #F29A0C, #E97818)",
                          boxShadow: "0 6px 18px rgba(242, 154, 12, 0.45)"
                        }}>
                        <GiLotus size={22} className="text-white" />
                      </motion.div>
                      <div>
                        <p className="text-[#F29A0C] text-[10.5px] font-black uppercase tracking-[2.2px] leading-tight mb-0.5">
                          Daily Wisdom
                        </p>
                        <span className="text-white/85 text-[11px] font-bold leading-none block">
                          {wisdom.tag}
                        </span>
                      </div>
                    </div>

                    <span className="px-3.5 py-1.5 rounded-full text-[9.5px] font-black uppercase tracking-wider text-white shadow-md"
                      style={{ background: "linear-gradient(135deg, #F29A0C, #E97818)", boxShadow: "0 4px 14px rgba(242,154,12,0.35)" }}>
                      Live Guidance
                    </span>
                  </div>

                  <div className="relative mb-4">
                    <span className="absolute -top-4 -left-1 text-[42px] font-serif leading-none opacity-30 text-[#F29A0C]">“</span>
                    <p className="text-white font-extrabold text-[15px] leading-[1.62] relative z-10 pl-4 italic"
                      style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {wisdom.quote}
                    </p>
                  </div>
                </div>

                <div>
                  <div className="rounded-[20px] p-4 mb-3.5 relative overflow-hidden backdrop-blur-xl"
                    style={{
                      background: "rgba(242, 154, 12, 0.14)",
                      border: "1.5px solid rgba(242, 154, 12, 0.32)",
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2)"
                    }}>
                    <div className="flex items-center gap-1.5 text-[#F29A0C] text-[10.5px] font-black uppercase tracking-wider mb-1.5">
                      <WisdomIcon size={13} />
                      <span>Today's Intention</span>
                    </div>
                    <p className="text-white text-[13px] font-bold leading-relaxed">
                      {wisdom.intention}
                    </p>
                  </div>

                  <button onClick={onExploreMeditation || onExploreClasses}
                    className="w-full h-11 rounded-full font-extrabold text-[12.5px] text-white flex items-center justify-center gap-2 cursor-pointer transition-all hover:scale-[1.02] active:scale-95 shadow-xl"
                    style={{
                      background: "linear-gradient(135deg, #F29A0C, #E97818)",
                      boxShadow: "0 10px 28px rgba(242, 154, 12, 0.4)",
                      fontFamily: "'Inter', sans-serif"
                    }}>
                    <span>Explore Meditation</span>
                    <FiArrowRight size={14} />
                  </button>
                </div>
              </motion.div>
            </TiltCard>

            {/* ── ROW 2: CHECKED IN TODAY CARD (COMPACT SLEEK REDUCED HEIGHT) ── */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, delay: 0.15 }}
              className="rounded-[24px] p-4 flex flex-col justify-between flex-shrink-0"
              style={{
                background: "rgba(238, 246, 235, 0.95)",
                border: "1.5px solid #D5E7CF",
                boxShadow: "0 12px 30px rgba(107, 138, 90, 0.08)"
              }}>
              <div>
                <div className="flex justify-between items-center">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold text-[#4B733D] bg-[#DFEFD9] border border-[#CCE2C4] tracking-wider uppercase">
                    <FiCheck size={11} className="text-[#4B733D]" />
                    <span>CHECKED IN TODAY</span>
                  </span>

                  <div className="w-7 h-7 rounded-full bg-[#5D804E] flex items-center justify-center text-white shadow-sm">
                    <FiCheck size={14} className="stroke-[2.5]" />
                  </div>
                </div>

                <h3 className="text-[15px] font-black text-[#1C2618] mt-2 mb-0.5 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Namaste, {firstName}
                </h3>

                <p className="text-[11.5px] text-[#4A5745] font-medium leading-[1.45] mb-2">
                  "Your daily check-in is complete! Consistency builds absolute mental clarity and physical strength."
                </p>
              </div>

              <div className="pt-2 border-t border-[#DDECD8] flex items-center gap-1.5 text-[#4B733D]">
                <GiLotus size={14} className="text-[#5D804E] flex-shrink-0" />
                <span className="text-[11px] font-bold text-[#4B733D]">
                  Keep taking mindful pauses throughout your day.
                </span>
              </div>
            </motion.div>
          </div>
        );
      })()}
    </section>
  );
}

/* ─── STATS BAND ─────────────────────────────────────────────────────── */
function StatsBand({ profile, mindfulnessTotal = 0, achievementSummary = null, userGoals = [], dietData = null }) {
  const navigate = useNavigate();

  // 1. Mindfulness Minutes (From DB)
  const mindfulnessMinutes = mindfulnessTotal || 0;

  // 2. Certificates Earned (From real DB Achievement Summary)
  const certificatesList = achievementSummary?.certificates || achievementSummary?.Certificates || [];
  const certificatesEarnedCount = certificatesList.filter(c => c.unlocked || c.Unlocked).length;
  const totalCertificatesCount = certificatesList.length;

  // 3. Diet Meals Logged (From real DB UserMealLogs via DietPlan API)
  const completedMealsCount = dietData?.completedLogs?.length ?? dietData?.completedMeals ?? 0;
  const totalDietMeals = dietData?.totalMeals ?? dietData?.meals?.length ?? 0;

  // 4. Goals Completed (From real DB UserGoals)
  const rawGoalsList = Array.isArray(userGoals) ? userGoals : [];
  const uniqueGoalsMap = new Map();
  rawGoalsList.forEach(g => {
    const key = (g.title || g.Title || '').trim().toLowerCase();
    if (key && !uniqueGoalsMap.has(key)) {
      uniqueGoalsMap.set(key, g);
    }
  });
  const goalsList = uniqueGoalsMap.size > 0 ? Array.from(uniqueGoalsMap.values()) : rawGoalsList;
  const goalsCompletedCount = goalsList.filter(g => g.isCompleted || g.IsCompleted || ((g.target || g.Target) > 0 && (g.current || g.Current) >= (g.target || g.Target))).length;
  const totalGoalsCount = goalsList.length;

  // 5. Achievements Done (From real DB Badges summary)
  const badgesList = achievementSummary?.badges || achievementSummary?.Badges || [];
  const achievementsDoneCount = achievementSummary?.unlockedCount ?? achievementSummary?.UnlockedCount ?? badgesList.filter(b => b.unlocked || b.Unlocked).length;
  const totalBadgesCount = badgesList.length;

  const stats = [
    { 
      icon: FiHeart,    
      val: mindfulnessMinutes,  
      unit: "Mindfulness Mins", 
      note: `${mindfulnessMinutes} mins logged`, 
      accent: "#F29A0C", 
      sub: "m",
      onClick: () => navigate("/user/traininglibrary")
    },
    { 
      icon: FiShield,   
      val: certificatesEarnedCount, 
      unit: "Certificates Earned", 
      note: totalCertificatesCount > 0 ? `${certificatesEarnedCount} of ${totalCertificatesCount} unlocked` : `${certificatesEarnedCount} unlocked`, 
      accent: "#6B8A5A", 
      sub: totalCertificatesCount > 0 ? `/${totalCertificatesCount}` : "",
      onClick: () => navigate("/user/achievements")
    },
    { 
      icon: FiCoffee,   
      val: completedMealsCount, 
      unit: "Diet Meals Logged", 
      note: totalDietMeals > 0 ? `${completedMealsCount} of ${totalDietMeals} logged` : `${completedMealsCount} meals logged`, 
      accent: "#F29A0C", 
      sub: totalDietMeals > 0 ? `/${totalDietMeals}` : "",
      onClick: () => navigate("/user/dietplan")
    },
    { 
      icon: FiTarget,   
      val: goalsCompletedCount, 
      unit: "Goals Completed", 
      note: totalGoalsCount > 0 ? `${goalsCompletedCount} of ${totalGoalsCount} achieved` : `${goalsCompletedCount} active goals`, 
      accent: "#6B8A5A", 
      sub: totalGoalsCount > 0 ? `/${totalGoalsCount}` : "",
      onClick: () => navigate("/user/goals")
    },
    { 
      icon: FiAward,    
      val: achievementsDoneCount, 
      unit: "Achievements Done", 
      note: totalBadgesCount > 0 ? `${achievementsDoneCount} of ${totalBadgesCount} badges` : `${achievementsDoneCount} badges unlocked`, 
      accent: "#F29A0C", 
      sub: totalBadgesCount > 0 ? `/${totalBadgesCount}` : "",
      onClick: () => navigate("/user/achievements")
    },
  ];

  return (
    <div className="ud-kpi ud-kpi-dash grid grid-cols-2 md:grid-cols-5 gap-3.5 mb-6">
      {stats.map((s, i) => (
        <motion.div key={s.unit}
          initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay: 0.12 + i*0.06, duration:0.35, ease:[0.25,1,0.5,1] }}
          whileHover={{ y:-4, boxShadow:"0 20px 40px -10px rgba(0,0,0,0.1)" }}
          onClick={s.onClick}
          role="button"
          tabIndex={0}
          className="group relative overflow-hidden p-5 rounded-[24px] border transition-all duration-300 flex flex-col justify-between cursor-pointer"
          style={{
            background: "rgba(255, 255, 255, 0.88)",
            borderColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            boxShadow: "0 10px 30px -5px rgba(0,0,0,0.04), 0 0 0 1px rgba(239,229,216,0.5)"
          }}>
          {/* Ambient micro-glow spotlight */}
          <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-25 group-hover:opacity-50 transition-opacity duration-500 pointer-events-none"
            style={{ background: s.accent, filter:"blur(26px)" }} />

          <div className="ud-kpi-body">
            {/* Top row: Icon + Pill */}
            <div className="ud-kpi-iconrow flex items-center justify-between mb-3.5">
              <div className="ud-kpi-icon w-11 h-11 rounded-[14px] flex items-center justify-center flex-shrink-0 text-white shadow-sm transition-transform duration-300 group-hover:scale-105"
                style={{ background: `linear-gradient(135deg, ${s.accent}, ${s.accent}DD)`, boxShadow: `0 6px 16px ${s.accent}30` }}>
                <s.icon style={{ color:"#fff", fontSize:18 }} />
              </div>

              <span className="ud-kpi-note text-[9.5px] font-black px-2 py-0.5 rounded-full border truncate max-w-[110px]"
                style={{ background: `${s.accent}12`, color: s.accent, borderColor: `${s.accent}25` }}>
                {s.note}
              </span>
            </div>

            {/* Metric Unit */}
            <p className="ud-kpi-lbl text-[10px] font-black text-zinc-400 uppercase tracking-[0.9px] mb-1">
              {s.unit}
            </p>

            {/* Big Value */}
            <p className="ud-kpi-val text-[32px] font-black text-[#1F1F1F] leading-none flex items-baseline gap-0.5"
              style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-1.3px" }}>
              <AnimCount to={s.val} duration={900 + i*100} />
              {s.sub && <span className="text-[14px] text-[#AAA] font-normal">{s.sub}</span>}
            </p>
          </div>

          {/* Bottom Accent Bar */}
          <div className="absolute bottom-0 left-0 right-0 h-[3px] opacity-70 group-hover:opacity-100 transition-opacity duration-300 rounded-b-[24px]"
            style={{ background: `linear-gradient(90deg, ${s.accent}, ${s.accent}88)` }} />
        </motion.div>
      ))}
    </div>
  );
}

/* ─── MONTHLY PROGRESS CARD ─────────────────────────────────────────── */
function MonthlyProgressCard({ sessions = [], attendanceHistory = [], liveProgress = null, userGoals = [], progressPercent = 0 }) {
  const { stats } = useCheckIn();

  const now = useMemo(() => new Date(), []);
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const currentMonthName = now.toLocaleString("en-US", { month: "long" });

  // 1. Calculate actual sessions/practices attended in THIS current calendar month from real attendance records
  const monthlyAttendedCount = useMemo(() => {
    if (!attendanceHistory || !Array.isArray(attendanceHistory) || attendanceHistory.length === 0) {
      if (sessions && Array.isArray(sessions)) {
        return sessions.filter(s => s.isCheckedIn).length;
      }
      return 0;
    }
    const thisMonthAtts = attendanceHistory.filter(a => {
      if (!a.attendanceDate) return false;
      const d = new Date(a.attendanceDate);
      return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
    });
    // Distinct calendar days checked in this month
    const uniqueDates = new Set(thisMonthAtts.map(a => {
      const d = new Date(a.attendanceDate);
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    }));
    return uniqueDates.size;
  }, [attendanceHistory, currentYear, currentMonth, sessions]);

  // 2. Dynamic Monthly Goal (from Super Admin scheduled live sessions this month, or user-set goals)
  const monthlyGoal = useMemo(() => {
    if (sessions && Array.isArray(sessions) && sessions.length > 0) {
      const thisMonthSessions = sessions.filter(s => {
        const dateStr = s.scheduledAt || s.date || s.startTime;
        if (!dateStr) return true;
        const d = new Date(dateStr);
        return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
      });
      if (thisMonthSessions.length > 0) {
        return thisMonthSessions.length;
      }
      return sessions.length;
    }
    if (userGoals && Array.isArray(userGoals)) {
      const sessionGoal = userGoals.find(g => 
        (g.title || "").toLowerCase().includes("session") || 
        (g.category || "").toLowerCase().includes("session") ||
        (g.type || "").toLowerCase().includes("month")
      );
      if (sessionGoal && (sessionGoal.target || sessionGoal.Target) > 0) {
        return sessionGoal.target || sessionGoal.Target;
      }
    }
    return 0; // If no sessions scheduled by super admin yet, goal is 0
  }, [sessions, currentYear, currentMonth, userGoals]);

  // 3. Dynamic outer progress percentage (Sessions Goal completion)
  const goalPercent = monthlyGoal > 0 
    ? Math.min(100, Math.round((monthlyAttendedCount / monthlyGoal) * 100))
    : (monthlyAttendedCount > 0 ? 100 : 0);

  // 4. Dynamic inner progress percentage (Wellness Consistency Score)
  const wellnessScore = stats?.wellnessScore ?? (liveProgress?.wellnessScore ?? 0);
  const consistencyScorePercent = Math.min(100, Math.max(0, wellnessScore));

  // 5. Dynamic Badge milestone
  const badgeInfo = useMemo(() => {
    if (goalPercent >= 80) return { label: "Gold Yogi", sub: "Top tier this month!", icon: FiAward, bg: "linear-gradient(135deg,#F29A0C,#E97818)", shadow: "0 8px 20px rgba(242,154,12,0.32)" };
    if (goalPercent >= 50) return { label: "Silver Yogi", sub: "Strong rhythm in progress!", icon: FiStar, bg: "linear-gradient(135deg,#6B8A5A,#8BA47A)", shadow: "0 8px 20px rgba(107,138,90,0.28)" };
    if (goalPercent > 0) return { label: "Bronze Yogi", sub: "Great consistency starting!", icon: FiTrendingUp, bg: "linear-gradient(135deg,#E97818,#C8660A)", shadow: "0 8px 20px rgba(233,120,24,0.28)" };
    return { label: "Monthly Journey", sub: "Join scheduled sessions to build streak!", icon: FiZap, bg: "linear-gradient(135deg,#788B70,#5D6E56)", shadow: "0 8px 20px rgba(107,138,90,0.18)" };
  }, [goalPercent]);

  // SVG Geometry for precise circular stroke offset
  // Outer circle: r = 80, C = 2 * PI * 80 ≈ 502.65
  const circOuter = 502.65;
  const offsetOuter = circOuter - (circOuter * (monthlyGoal > 0 ? (Math.min(monthlyAttendedCount, monthlyGoal) / monthlyGoal) : 0));

  // Inner circle: r = 58, C = 2 * PI * 58 ≈ 364.42
  const circInner = 364.42;
  const offsetInner = circInner - (circInner * (consistencyScorePercent / 100));

  return (
    <motion.div initial={{ opacity:0, x:24 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }}
      className="rounded-[36px] p-7 md:p-8 flex flex-col justify-between" style={glass}>
      <div>
        <div className="flex justify-between items-center mb-1">
          <h3 className="text-[18px] font-bold text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>Monthly Progress</h3>
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/10 text-amber-700 border border-amber-500/20">
            {currentMonthName}
          </span>
        </div>
        <p className="text-[12px] text-[#AAA] mb-5">
          {monthlyGoal > 0 ? `Target: ${monthlyGoal} scheduled session${monthlyGoal === 1 ? '' : 's'} this month` : "No live sessions scheduled this month"}
        </p>

        {/* Dual Dynamic Circular Progress Bar */}
        <div className="relative w-48 h-48 mx-auto mb-4">
          <svg className="absolute inset-0 w-full h-full" style={{ transform:"rotate(-90deg)" }} viewBox="0 0 192 192">
            {/* Outer Track & Progress (Sessions Goal) */}
            <circle cx="96" cy="96" r="80" stroke="#F1E8DA" strokeWidth="11" fill="none" opacity="0.85"/>
            <motion.circle cx="96" cy="96" r="80" stroke="url(#pg1)" strokeWidth="11" fill="none" strokeLinecap="round"
              strokeDasharray={circOuter}
              initial={{ strokeDashoffset: circOuter }}
              animate={{ strokeDashoffset: offsetOuter }}
              transition={{ duration: 1.2, ease: "easeOut" }}/>
            
            {/* Inner Track & Progress (Consistency & Wellness Score) */}
            <circle cx="96" cy="96" r="58" stroke="#ECF5E8" strokeWidth="8" fill="none" opacity="0.85"/>
            <motion.circle cx="96" cy="96" r="58" stroke="url(#pg2)" strokeWidth="8" fill="none" strokeLinecap="round"
              strokeDasharray={circInner}
              initial={{ strokeDashoffset: circInner }}
              animate={{ strokeDashoffset: offsetInner }}
              transition={{ duration: 1.2, ease: "easeOut", delay: 0.1 }}/>
            
            <defs>
              <linearGradient id="pg1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F29A0C"/>
                <stop offset="100%" stopColor="#E97818"/>
              </linearGradient>
              <linearGradient id="pg2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#6B8A5A"/>
                <stop offset="100%" stopColor="#8BA47A"/>
              </linearGradient>
            </defs>
          </svg>
          
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <p className="text-[42px] font-black text-[#1F1F1F] leading-none" style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-1.5px" }}>
              <AnimCount to={goalPercent} duration={1200} />%
            </p>
            <p className="text-[10.5px] font-bold text-[#AAA] tracking-wide uppercase mt-1">Goal Completed</p>
          </div>
        </div>

        {/* Dynamic Legend */}
        <div className="flex items-center justify-center gap-3.5 mb-4 text-[10.5px] font-bold">
          <div className="flex items-center gap-1.5 text-zinc-600">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)" }} />
            <span>Sessions ({goalPercent}%)</span>
          </div>
          <div className="flex items-center gap-1.5 text-zinc-600">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "linear-gradient(135deg,#6B8A5A,#8BA47A)" }} />
            <span>Score ({consistencyScorePercent}%)</span>
          </div>
        </div>

        {/* Dynamic Metric Cards */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-[18px] p-3.5 border border-[#6B8A5A]/15" style={{ background:"#F5FAF2" }}>
            <p className="text-[10px] text-[#6B8A5A] font-extrabold uppercase tracking-wide">Sessions Attended</p>
            <p className="text-[20px] font-black text-[#1F1F1F] mt-0.5" style={{ fontFamily:"'Poppins',sans-serif" }}>
              {monthlyAttendedCount} <span className="text-[12px] text-[#AAA] font-normal">/ {monthlyGoal}</span>
            </p>
          </div>
          <div className="rounded-[18px] p-3.5 border border-[#E97818]/15" style={{ background:"#FFF4E8" }}>
            <p className="text-[10px] text-[#E97818] font-extrabold uppercase tracking-wide">Wellness Score</p>
            <p className="text-[20px] font-black text-[#1F1F1F] mt-0.5" style={{ fontFamily:"'Poppins',sans-serif" }}>
              {wellnessScore} <span className="text-[12px] text-[#AAA] font-normal">/ 100</span>
            </p>
          </div>
        </div>
      </div>

      {/* Dynamic Badge Status */}
      <div className="rounded-[18px] p-3.5 flex items-center gap-3 text-white"
        style={{ background: badgeInfo.bg, boxShadow: badgeInfo.shadow }}>
        <badgeInfo.icon style={{ color:"#fff", fontSize:20, flexShrink:0 }} />
        <div className="min-w-0">
          <p className="text-white/80 text-[10px] font-bold uppercase tracking-wider">Monthly Tier Status</p>
          <p className="text-white text-[13px] font-black truncate">{badgeInfo.label} · <span className="font-normal text-white/90 text-[11.5px]">{badgeInfo.sub}</span></p>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── TODAY PRACTICE ─────────────────────────────────────────────────── */
function TodayPractice({ videos = [], videoProgressMap = {}, onPlayVideo, sessions = [], attendanceHistory = [], liveProgress = null, userGoals = [] }) {
  const navigate = useNavigate();
  const pausedVideo = useMemo(() => {
    if (!videos || videos.length === 0 || !videoProgressMap) return null;
    let targetVideo = null;
    let highestProgress = 0;
    videos.forEach(v => {
      const prog = videoProgressMap[v.id.toString()];
      if (prog && prog > 0 && prog < 100) {
        if (prog > highestProgress) {
          highestProgress = prog;
          targetVideo = v;
        }
      }
    });
    return targetVideo;
  }, [videos, videoProgressMap]);

  const currentVideo = pausedVideo || (videos && videos.length > 0 ? videos[0] : null);

  const parsedVideo = useMemo(() => {
    if (!currentVideo) return { desc: "", instructor: "Instructor", duration: "30 Minutes" };
    const defaultData = {
      desc: currentVideo.description || "Daily guided practice video.",
      instructor: currentVideo.instructor || "Instructor",
      duration: `${currentVideo.durationInMinutes || 30} Minutes`
    };

    if (currentVideo.description && currentVideo.description.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(currentVideo.description);
        defaultData.desc = parsed.desc || parsed.Description || "";
        if (parsed.instructor) {
          defaultData.instructor = parsed.instructor;
        }
        if (parsed.durationStr) {
          defaultData.duration = parsed.durationStr;
        } else if (parsed.duration) {
          defaultData.duration = `${parsed.duration} Minutes`;
        }
      } catch (e) {
        console.error(e);
      }
    }
    return defaultData;
  }, [currentVideo]);

  const progressPercent = useMemo(() => {
    if (!pausedVideo) return 0;
    return videoProgressMap[pausedVideo.id.toString()] || 0;
  }, [pausedVideo, videoProgressMap]);

  if (!currentVideo) {
    return (
      <div className="grid xl:grid-cols-[1fr_360px] gap-5 mb-5">
        <motion.div initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
          className="relative overflow-hidden rounded-[28px] sm:rounded-[36px] p-6 sm:p-9 flex flex-col justify-center items-center text-center" style={glass}>
          <GiLotus className="text-[#E97818] mb-3" size={42} />
          <h3 className="text-[22px] font-black text-[#1F1F1F] mb-2" style={{ fontFamily:"'Poppins',sans-serif" }}>Explore Practice Library</h3>
          <p className="text-[14px] text-[#999] max-w-md mb-6 leading-relaxed">
            Unlock on-demand mindfulness and yoga training videos curated for your wellness journey.
          </p>
          <button onClick={() => navigate("/user/traininglibrary")}
            className="h-11 px-8 rounded-full text-white font-bold text-sm flex items-center gap-2 cursor-pointer"
            style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", boxShadow: "0 10px 24px rgba(242,154,12,0.35)" }}>
            Browse All Practice Videos <FiArrowRight size={14} />
          </button>
        </motion.div>
        <MonthlyProgressCard 
          sessions={sessions} 
          attendanceHistory={attendanceHistory} 
          liveProgress={liveProgress} 
          userGoals={userGoals} 
          progressPercent={0} 
        />
      </div>
    );
  }

  return (
    <div className="grid xl:grid-cols-[1fr_360px] gap-5 mb-5">
      <motion.div initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
        className="relative overflow-hidden rounded-[28px] sm:rounded-[36px] p-6 sm:p-9" style={glass}>
        <div className="absolute top-0 right-0 w-52 h-52 pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.1),transparent)", filter:"blur(40px)" }} />
        <div className="absolute inset-0 pointer-events-none opacity-[0.03] rounded-[inherit]"
          style={{ backgroundImage:"radial-gradient(circle,#000 1px,transparent 0)", backgroundSize:"22px 22px" }} />

        <div className="flex items-start justify-between mb-6">
          <div>
            <span className="inline-block px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide text-[#C8660A] mb-4"
              style={{ background:"rgba(242,154,12,0.1)" }}>
              {pausedVideo ? "Continue Watching" : "Today's Recommendation"}
            </span>
            <h2 className="text-[36px] font-black text-[#1F1F1F] leading-tight mb-3"
              style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-1px" }}>{currentVideo.title}</h2>
            <p className="text-[14px] text-[#999] leading-[1.8] max-w-[440px]">
              {parsedVideo.desc}
            </p>
          </div>
          <motion.div animate={{ scale:[1,1.08,1] }} transition={{ duration:3, repeat:Infinity, ease:"easeInOut" }}
            onClick={() => onPlayVideo(currentVideo)}
            className="w-14 h-14 rounded-full flex items-center justify-center text-white flex-shrink-0 cursor-pointer"
            style={{ background:"linear-gradient(135deg,#F29A0C,#E97818)", boxShadow:"0 12px 28px rgba(242,154,12,0.45)" }}>
            <FiPlay size={20} />
          </motion.div>
        </div>

        <div className="flex flex-wrap gap-2.5 mb-7">
          {[
            ["clock", parsedVideo.duration, "#F29A0C"],
            ["user", `Instructor ${parsedVideo.instructor}`, "#F29A0C"],
            ["star", currentVideo.averageRating || currentVideo.rating ? `${currentVideo.averageRating || currentVideo.rating} Rating` : "Recommended", "#6B8A5A"]
          ].map(([ic,txt,c]) => (
            <div key={ic} className="flex items-center gap-2 px-4 py-2 rounded-full text-[13px] text-[#555]"
              style={{ background:"rgba(255,248,238,0.9)", border:`1px solid ${c}1F` }}>
              <span style={{ color:c }}>
                {ic === "clock" ? <FiClock size={14}/> : ic === "user" ? <FiUser size={14}/> : <FiStar size={14}/>}
              </span>
              {txt}
            </div>
          ))}
        </div>

        <div className="rounded-[18px] p-4 mb-7" style={{ background:"rgba(255,248,238,0.6)" }}>
          <p className="text-[10px] font-bold text-[#AAA] uppercase tracking-[0.8px] mb-3">
            {pausedVideo ? `Practice Progress - ${progressPercent}%` : "Session Flow"}
          </p>
          <div className="flex rounded-lg overflow-hidden h-2 bg-zinc-200">
            {pausedVideo ? (
              <div className="h-full rounded-full" style={{ width: `${progressPercent}%`, background: "linear-gradient(90deg,#F29A0C,#E97818)" }} />
            ) : (
              <>
                <div className="flex-[2]" style={{ background:"linear-gradient(90deg,#F29A0C,#E97818)" }} />
                <div className="flex-[1]" style={{ background:"linear-gradient(90deg,#E97818,#6B8A5A)" }} />
                <div className="flex-[1] opacity-40" style={{ background:"linear-gradient(90deg,#6B8A5A,#8BA47A)" }} />
              </>
            )}
          </div>
          {!pausedVideo && (
            <div className="flex justify-between mt-1.5">
              <span className="text-[10px] text-[#E97818] font-bold">Warmup</span>
              <span className="text-[10px] text-[#6B8A5A] font-bold">Core</span>
              <span className="text-[10px] text-[#AAA] font-bold">Cooldown</span>
            </div>
          )}
        </div>

        <motion.button whileHover={{ y:-3, scale:1.02 }} whileTap={{ scale:0.98 }}
          onClick={() => onPlayVideo(currentVideo)}
          className="h-[52px] px-8 rounded-full text-white font-bold text-[14px] flex items-center gap-3 cursor-pointer"
          style={{ background:"linear-gradient(135deg,#F29A0C,#E97818)", boxShadow:"0 16px 40px rgba(242,154,12,0.38)", fontFamily:"'Inter',sans-serif" }}>
          <FiPlay size={15}/> {pausedVideo ? "Resume Practice" : "Start Practice"}
        </motion.button>
      </motion.div>

      <MonthlyProgressCard 
        sessions={sessions} 
        attendanceHistory={attendanceHistory} 
        liveProgress={liveProgress} 
        userGoals={userGoals} 
        progressPercent={progressPercent} 
      />
    </div>
  );
}

/* ─── DATE HELPERS ────────────────────────────────────────────────── */
const parseUtcDate = (dateStr) => {
  if (!dateStr) return new Date();
  if (!dateStr.endsWith("Z") && !dateStr.includes("+") && !dateStr.includes("-")) {
    return new Date(dateStr + "Z");
  }
  return new Date(dateStr);
};

function formatSessionTimeWithZone(date) {
  const localTimeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  let localTz = "";
  try {
    const parts = new Intl.DateTimeFormat([], { timeZoneName: "short" }).formatToParts(date);
    const tzPart = parts.find(p => p.type === "timeZoneName");
    if (tzPart) localTz = tzPart.value;
  } catch (e) {
    console.error(e);
  }

  const istTimeStr = new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  }).format(date) + " IST";

  if (localTz && localTz !== "GMT+5:30" && localTz !== "IST") {
    return `${localTimeStr} ${localTz} (${istTimeStr})`;
  }
  return `${localTimeStr} IST`;
}

function formatDate(date) {
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(date); d.setHours(0,0,0,0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return date.toLocaleDateString("en-IN", { weekday: "short", month: "short", day: "numeric" });
}

const calculateStreak = (attendances) => {
  if (!attendances || attendances.length === 0) return 0;
  
  // Extract unique dates sorted descending
  const dates = Array.from(new Set(
    attendances.map(a => new Date(a.attendanceDate).toDateString())
  )).map(d => new Date(d)).sort((a, b) => b - a);
  
  const today = new Date();
  today.setHours(0,0,0,0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  let currentStreak = 0;
  let expectedDate = new Date(dates[0]); // start from the most recent check-in date
  expectedDate.setHours(0,0,0,0);
  
  // If the most recent check-in is older than yesterday, the streak is broken (0)
  if (expectedDate < yesterday) {
    return 0;
  }
  
  for (let i = 0; i < dates.length; i++) {
    const checkInDate = new Date(dates[i]);
    checkInDate.setHours(0,0,0,0);
    
    const diffTime = Math.abs(expectedDate - checkInDate);
    const diffDays = Math.ceil(diffTime / 86400000);
    
    if (diffDays === 0) {
      currentStreak++;
      expectedDate.setDate(expectedDate.getDate() - 1);
    } else if (diffDays === 1) {
      currentStreak++;
      expectedDate = new Date(checkInDate);
      expectedDate.setDate(expectedDate.getDate() - 1);
    } else {
      break;
    }
  }
  return currentStreak;
};

/* ─── UPCOMING SESSIONS ──────────────────────────────────────────────── */
function UpcomingSessions({ sessions, userTier = "Free", userRank = 0, packages = [], onBook, onCancel, onJoin, onUpgrade, onExplore }) {
  const activeSessions = useMemo(() => {
    if (!sessions) return [];
    return sessions
      .filter(s => s.isLive || s.isUpcoming)
      .map(s => {
        const parsedDate = parseUtcDate(s.scheduledAt);
        return {
          id: s.id,
          title: s.title,
          isLive: s.isLive,
          isCheckedIn: s.isCheckedIn,
          tier: s.tier || "Free",
          instructor: s.instructorName || s.instructor || "Instructor",
          dateStr: formatDate(parsedDate),
          timeStr: formatSessionTimeWithZone(parsedDate),
          duration: s.durationInMinutes || 60
        };
      })
      .slice(0, 4);
  }, [sessions]);

  return (
    <section className="mb-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[22px] font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.5px" }}>Upcoming Sessions</h2>
          <p className="text-[13px] text-[#AAA] mt-0.5">Book slots or join live classes instantly</p>
        </div>
        <button onClick={onExplore} className="h-10 px-5 rounded-full text-[13px] font-semibold text-[#555] cursor-pointer"
          style={{ background:"rgba(255,255,255,0.9)", border:"1px solid rgba(239,229,216,0.8)" }}>
          View Schedule <FiArrowRight style={{ display:"inline", marginLeft:4 }} />
        </button>
      </div>

      {activeSessions.length === 0 ? (
        <div className="py-10 text-center rounded-[28px] border border-dashed border-zinc-300" style={glass}>
          <FiCalendar size={28} className="mx-auto mb-2 text-[#AAA]" />
          <p className="text-zinc-500 text-[14px] font-semibold">No live or upcoming sessions scheduled today</p>
          <button onClick={onExplore} className="mt-3 px-4 py-2 rounded-full text-[12px] text-white font-bold bg-[#E97818] cursor-pointer">
            Check Main Calendar
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {activeSessions.map((s, i) => {
            const isLive = s.isLive;
            const isAccessible = canUserAccessContent(s.tier, userTier, userRank, packages);

            return (
              <TiltCard key={s.id} intensity={isLive ? 5 : 7}
                className="relative overflow-hidden rounded-[28px] p-5 cursor-default flex flex-col justify-between min-h-[210px]"
                style={isLive ? { background:"linear-gradient(145deg,#F29A0C,#E97818)", boxShadow:"0 16px 40px rgba(242,154,12,0.38)" } : { ...glass }}>
                <motion.div className="flex-1 flex flex-col justify-between" initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ delay: i*0.08 }}>
                  <div>
                    {!isLive && (
                      <div className="absolute top-0 right-0 w-24 h-24 rounded-full pointer-events-none"
                        style={{ background:`radial-gradient(circle,#F29A0C15,transparent)`, filter:"blur(20px)", transform:"translate(30%,-30%)" }} />
                    )}
                    <div className="flex justify-between items-start mb-3">
                      <div className="w-10 h-10 rounded-[14px] flex items-center justify-center"
                        style={isLive ? { background:"rgba(255,255,255,0.2)" } : { background:`rgba(242,154,12,0.1)` }}>
                        <FiCalendar style={{ color: isLive ? "#fff" : "#F29A0C", fontSize:16 }} />
                      </div>
                      
                      <div className="flex items-center gap-1.5">
                        {isLive && (
                          <span className="text-[9px] font-black tracking-widest text-white uppercase px-2 py-0.5 rounded-full bg-red-600 animate-pulse">
                            LIVE NOW
                          </span>
                        )}
                        {s.tier && s.tier.toLowerCase() !== "free" && (
                          <span className={`text-[9px] font-black tracking-wider uppercase px-2.5 py-0.5 rounded-full flex items-center gap-1 ${
                            isLive 
                              ? "bg-white/20 text-white" 
                              : isAccessible 
                                ? "bg-amber-50 text-amber-800 border border-amber-200" 
                                : "bg-zinc-100 text-zinc-600 border border-zinc-300"
                          }`}>
                            {!isAccessible && <FiLock size={8} />}
                            {s.tier}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <h3 className="text-[14px] font-black mb-1.5 line-clamp-1" style={{ color: isLive ? "#fff" : "#1F1F1F" }}>{s.title}</h3>
                    <p className="text-[11px] mb-4" style={{ color: isLive ? "rgba(255,255,255,0.76)" : "#AAA" }}>
                      {s.dateStr} · {s.timeStr} ({s.duration} mins)
                    </p>
                  </div>
                  
                  <div>
                    {!isAccessible ? (
                      <button onClick={() => onUpgrade && onUpgrade(s)}
                        className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-full py-2 text-[11px] font-black shadow-md transition-transform active:scale-95 cursor-pointer flex items-center justify-center gap-1.5">
                        <FiLock size={12} /> Unlock with {s.tier || "Plan"}
                      </button>
                    ) : isLive ? (
                      <button onClick={() => onJoin(s.id)}
                        className="w-full bg-white hover:bg-zinc-50 text-[#E97818] rounded-full py-2 text-[11px] font-black shadow-sm transition-transform active:scale-95 cursor-pointer">
                        Join Class
                      </button>
                    ) : s.isCheckedIn ? (
                      <button onClick={() => onCancel(s.id)}
                        className="w-full border border-red-500/25 hover:border-red-500 bg-red-500/10 hover:bg-red-500/20 text-red-600 rounded-full py-2 text-[11px] font-bold transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-1">
                        <FiXCircle size={12} /> Cancel Slot
                      </button>
                    ) : (
                      <button onClick={() => onBook(s.id)}
                        className="w-full bg-[#E97818] hover:bg-[#C8660A] text-white rounded-full py-2 text-[11px] font-bold shadow-sm transition-transform active:scale-95 cursor-pointer">
                        Book Slot
                      </button>
                    )}
                  </div>
                </motion.div>
              </TiltCard>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ─── ACHIEVEMENTS ─────────────────────────────────────────────────── */
function Achievements({ profile }) {
  const navigate = useNavigate();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  const isPermitted = isFeaturePermitted(profile, "achievements");

  useEffect(() => {
    if (!isPermitted) {
      setLoading(false);
      return;
    }
    const fetchAchievements = async () => {
      try {
        const res = await achievementAPI.getSummary();
        if (res.data?.success && res.data?.data?.badges) {
          setBadges(res.data.data.badges);
        }
      } catch (err) {
        console.warn("Failed to fetch achievement summary for dashboard:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAchievements();
  }, [isPermitted]);

  // Compute 4 cards: First 2 = Latest Completed / Unlocked, Next 2 = Top In-Progress
  const displayCards = useMemo(() => {
    if (!badges || badges.length === 0) return [];

    const completed = badges.filter(b => b.unlocked);
    const inProgress = badges.filter(b => !b.unlocked);

    const top2Completed = completed.slice(0, 2);
    const top2InProgress = inProgress.slice(0, 2);

    const combined = [...top2Completed, ...top2InProgress];

    if (combined.length < 4) {
      const remaining = badges.filter(b => !combined.includes(b)).slice(0, 4 - combined.length);
      return [...combined, ...remaining];
    }

    return combined.slice(0, 4);
  }, [badges]);

  if (!isPermitted) {
    return (
      <section className="mb-8">
        <div className="rounded-[32px] p-8 text-center relative overflow-hidden" style={glass}>
          <div className="w-14 h-14 rounded-[20px] bg-amber-500/10 border border-amber-500/20 text-amber-600 flex items-center justify-center mx-auto mb-4">
            <FiLock size={26} />
          </div>
          <h3 className="text-[18px] font-black text-zinc-900 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Achievements & Badges (Locked)
          </h3>
          <p className="text-[13px] text-zinc-500 max-w-md mx-auto mb-5">
            Milestone badges and practice trophies are not enabled for your current tier. Upgrade your plan to unlock full achievements tracking.
          </p>
          <button
            onClick={() => navigate("/user/profile?tab=subscription")}
            className="h-10 px-6 rounded-full text-white font-extrabold text-[12.5px] bg-gradient-to-r from-amber-500 to-orange-500 shadow-md cursor-pointer inline-flex items-center gap-2"
          >
            <FiZap size={14} /> Upgrade Plan to Unlock Badges
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-[22px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
              Achievements & Badges
            </h2>
            <span className="px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-amber-500/10 text-amber-700 border border-amber-500/20">
              Live Progress
            </span>
          </div>
          <p className="text-[13px] text-[#AAA] mt-0.5">Your unlocked milestones & active in-progress badge challenges</p>
        </div>

        <motion.button
          whileHover={{ scale: 1.04, boxShadow: "0 10px 25px rgba(242,154,12,0.25)" }}
          whileTap={{ scale: 0.96 }}
          onClick={() => navigate("/user/achievements")}
          className="h-11 px-6 rounded-full text-[13px] font-extrabold text-[#E97818] inline-flex items-center gap-2 cursor-pointer self-start sm:self-auto"
          style={{
            background: "linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,248,238,0.95))",
            border: "1.5px solid rgba(242,154,12,0.35)",
            boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
            fontFamily: "'Inter', sans-serif"
          }}>
          <span>View All Achievements</span>
          <FiArrowRight size={14} />
        </motion.button>
      </div>

      {displayCards.length === 0 ? (
        <div className="rounded-[30px] p-8 text-center" style={glass}>
          <GiLotus className="text-[#E97818] mx-auto mb-3" size={36} />
          <h4 className="text-[16px] font-bold text-zinc-800 mb-1">Start Your Achievement Journey</h4>
          <p className="text-[13px] text-zinc-500 max-w-md mx-auto mb-4">
            Complete your daily check-ins and live yoga practices to unlock official achievement badges and milestones.
          </p>
          <button onClick={() => navigate("/user/achievements")} className="px-6 py-2.5 rounded-full text-white font-bold text-[12px] bg-gradient-to-r from-amber-500 to-orange-500 shadow-md cursor-pointer">
            Explore Achievements
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {displayCards.map((b, i) => {
          const isCompleted = b.unlocked;
          const accentColor = isCompleted ? (i === 0 ? "#F29A0C" : "#6B8A5A") : (i === 2 ? "#534AB7" : "#E97818");
          const IconComp = b.icon || (isCompleted ? FiAward : FiLock);

          return (
            <TiltCard key={b.id || b.title} intensity={6}
              className="relative overflow-hidden rounded-[30px] p-6 flex flex-col justify-between cursor-pointer transition-all duration-300"
              style={{
                ...glass,
                border: isCompleted ? `1.5px solid ${accentColor}40` : "1.5px dashed rgba(180,180,180,0.45)",
                background: isCompleted
                  ? "linear-gradient(145deg, rgba(255,255,255,0.95), rgba(255,251,245,0.95))"
                  : "linear-gradient(145deg, rgba(250,248,245,0.85), rgba(242,238,232,0.85))",
                boxShadow: isCompleted ? `0 16px 45px ${accentColor}15, inset 0 1px 0 #fff` : "0 8px 25px rgba(0,0,0,0.02)"
              }}>
              <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                
                {/* Header Tag */}
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5"
                    style={isCompleted ? {
                      background: `${accentColor}15`,
                      color: accentColor,
                      border: `1px solid ${accentColor}30`
                    } : {
                      background: "rgba(120,120,120,0.1)",
                      color: "#777",
                      border: "1px solid rgba(120,120,120,0.2)"
                    }}>
                    {isCompleted ? <FiCheck size={11} strokeWidth={3} /> : <FiClock size={11} />}
                    <span>{isCompleted ? "Unlocked" : "In Progress"}</span>
                  </span>

                  {b.pts && (
                    <span className="text-[10px] font-bold text-zinc-500 px-2.5 py-0.5 rounded-full bg-zinc-200/50">
                      +{b.pts} pts
                    </span>
                  )}
                </div>

                {/* Badge Emblem Icon */}
                <div className="w-16 h-16 rounded-[22px] flex items-center justify-center mx-auto mb-4 relative"
                  style={isCompleted ? {
                    background: `linear-gradient(135deg, ${accentColor}, ${accentColor}DD)`,
                    boxShadow: `0 10px 28px ${accentColor}45`
                  } : {
                    background: "linear-gradient(135deg, rgba(210,205,198,0.8), rgba(180,175,168,0.8))",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.06)"
                  }}>
                  <IconComp size={26} style={{ color: "#fff" }} />
                </div>

                {/* Badge Title & Description */}
                <h3 className="text-[15px] font-extrabold text-[#1F1F1F] text-center mb-1.5 leading-snug"
                  style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {b.title}
                </h3>
                <p className="text-[11px] font-medium text-[#777] text-center leading-relaxed mb-4 min-h-[32px]">
                  {b.desc || b.sub}
                </p>

                {/* Progress Bar / Date Tag */}
                {isCompleted ? (
                  <div className="mt-2 pt-3 border-t border-zinc-100 flex items-center justify-between text-[11px] font-bold"
                    style={{ color: accentColor }}>
                    <span>Milestone Completed</span>
                    <span>{b.date || "Earned"}</span>
                  </div>
                ) : (
                  <div className="mt-2 pt-3 border-t border-zinc-200/50">
                    <div className="flex justify-between items-center text-[11px] font-bold mb-1.5">
                      <span className="text-[#888]">Progress</span>
                      <span style={{ color: accentColor }}>{b.progress || 0}%</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(220,215,205,0.6)" }}>
                      <motion.div className="h-full rounded-full"
                        style={{
                          width: `${b.progress || 0}%`,
                          background: `linear-gradient(90deg, ${accentColor}, ${accentColor}CC)`,
                          boxShadow: `0 2px 8px ${accentColor}40`
                        }}
                        initial={{ width: 0 }}
                        animate={{ width: `${b.progress || 0}%` }}
                        transition={{ duration: 1, delay: i * 0.1 }} />
                    </div>
                  </div>
                )}
              </motion.div>
            </TiltCard>
          );
        })}
        </div>
      )}
    </section>
  );
}

/* ─── ACTIVITY + MEMBERSHIP PASS ───────────────────────────────────────── */
function ActivityAndWisdom({ 
  sessions = [], 
  profile, 
  daysLeft, 
  attendanceHistory = [], 
  videos = [], 
  videoProgressMap = {}, 
  dietData = null,
  onPlayVideo,
  onManageMembership 
}) {
  const { checkedIn, stats } = useCheckIn();
  const wellnessScore = stats?.wellnessScore ?? profile?.wellnessScore ?? 0;
  const [activeTab, setActiveTab] = useState("all");
  const [hoveredDay, setHoveredDay] = useState(null);

  // Dynamic real-time today index: 0=Mon, 1=Tue, 2=Wed, 3=Thu, 4=Fri, 5=Sat, 6=Sun
  const todayIdx = (new Date().getDay() + 6) % 7;

  const dayNames = [
    { short: "Mo", full: "Monday" },
    { short: "Tu", full: "Tuesday" },
    { short: "We", full: "Wednesday" },
    { short: "Th", full: "Thursday" },
    { short: "Fr", full: "Friday" },
    { short: "Sa", full: "Saturday" },
    { short: "Su", full: "Sunday" }
  ];

  // 1. Gather all Practice Videos Activity (Watched / In-Progress / Completed)
  const videoActivities = useMemo(() => {
    if (!videos || !videoProgressMap) return [];
    const watched = [];

    videos.forEach(v => {
      const vidKey = v.id?.toString();
      const progress = videoProgressMap[vidKey] ?? 0;
      if (progress > 0) {
        const isComplete = progress >= 90;
        const durationNum = parseInt(v.duration, 10) || 15;
        const minsPracticed = Math.max(2, Math.round((durationNum * progress) / 100));

        watched.push({
          id: `vid-${v.id}`,
          category: "video",
          type: isComplete ? "Completed Practice Video" : "Practice Video in Progress",
          title: v.title || "Yoga Practice Session",
          subtitle: `${v.category || "Vinyasa Flow"} · ${v.level || "All Levels"}`,
          time: isComplete ? "Completed" : `${progress}% Watched`,
          duration: `${minsPracticed}m practiced`,
          progress,
          mins: minsPracticed,
          videoObj: v,
          accent: "#8B5CF6",
          bg: "rgba(245,243,255,0.9)",
          border: "rgba(139,92,246,0.25)",
          ic: FiVideo
        });
      }
    });

    return watched;
  }, [videos, videoProgressMap]);

  // 2. Gather all Live Session Attendance Activity
  const sessionActivities = useMemo(() => {
    const list = [];
    sessions.filter(s => s.isCheckedIn).forEach(s => {
      const parsedDate = parseUtcDate(s.scheduledAt);
      const isPast = parsedDate < new Date();
      list.push({
        id: `sess-${s.id}`,
        category: "session",
        type: isPast ? "Live Session Attended" : "Live Session Booked",
        title: s.title || "Live Yoga Class",
        subtitle: `${s.instructorName || s.trainerName || "Divyyog Master"} · ${s.category || "Live Class"}`,
        time: `${formatDate(parsedDate)} · ${formatSessionTimeWithZone(parsedDate)}`,
        duration: `+${s.durationInMinutes || 45} mins`,
        mins: s.durationInMinutes || 45,
        accent: "#6B8A5A",
        bg: "rgba(245,250,242,0.9)",
        border: "rgba(107,138,90,0.25)",
        ic: FiCheck
      });
    });
    return list;
  }, [sessions]);

  // 3. Gather Diet Plan Activity
  const dietActivities = useMemo(() => {
    const list = [];
    const plan = dietData?.plan || dietData;
    const completedMeals = dietData?.completedMeals || plan?.completedMeals || [];

    if (completedMeals && Array.isArray(completedMeals) && completedMeals.length > 0) {
      completedMeals.slice(0, 4).forEach((m, idx) => {
        list.push({
          id: `diet-meal-${idx}`,
          category: "diet",
          type: "Diet Meal Logged",
          title: m.mealName || "Mindful Meal Logged",
          subtitle: `${m.calories ? `${m.calories} kcal` : "Calories not logged"} · ${m.day || "Tracked Meal"}`,
          time: "Meal Logged",
          duration: `${m.calories || 0} kcal`,
          accent: "#10B981",
          bg: "rgba(236,253,245,0.9)",
          border: "rgba(16,185,129,0.25)",
          ic: FiDroplet
        });
      });
    }

    if (plan?.title || plan?.planName || plan?.dietType) {
      list.unshift({
        id: "diet-plan-active",
        category: "diet",
        type: "Active Nutrition Plan",
        title: plan.title || plan.planName || `${plan.dietType || "Sattvic"} Nutrition Plan`,
        subtitle: `${plan.calorieGoal || "Balanced Nutrition"} · ${plan.targetDailyCal ? `Target: ${plan.targetDailyCal} kcal/day` : "Target not set"}`,
        time: "Active Plan",
        duration: `${plan.targetDailyCal || 0} kcal`,
        accent: "#10B981",
        bg: "rgba(236,253,245,0.9)",
        border: "rgba(16,185,129,0.25)",
        ic: FiDroplet
      });
    }

    return list;
  }, [dietData]);

  // 4. Gather Daily Check-In Activity
  const checkInActivities = useMemo(() => {
    const list = [];
    if (checkedIn) {
      list.push({
        id: "checkin-today",
        category: "checkin",
        type: "Daily Wellness Check-In",
        title: "Daily Wellness Check-In",
        subtitle: `Completed for Today · Current Streak: ${profile?.streak || stats?.streak || 1} Days`,
        time: "Today",
        duration: "+1 Streak Point",
        accent: "#F29A0C",
        bg: "rgba(255,244,232,0.9)",
        border: "rgba(242,154,12,0.25)",
        ic: FiSun
      });
    }

    if (attendanceHistory && Array.isArray(attendanceHistory)) {
      attendanceHistory.slice(0, 3).forEach((a, idx) => {
        const aDate = new Date(a.date || a.checkInDate || a.createdAt);
        list.push({
          id: `checkin-hist-${idx}`,
          category: "checkin",
          type: "Check-In Attendance",
          title: "Mindful Day Check-In",
          subtitle: `Verified Attendance · ${a.location || "Online"}`,
          time: formatDate(aDate),
          duration: "+1 Day Streak",
          accent: "#F29A0C",
          bg: "rgba(255,244,232,0.9)",
          border: "rgba(242,154,12,0.25)",
          ic: FiCheckCircle
        });
      });
    }

    return list;
  }, [checkedIn, profile, stats, attendanceHistory]);

  // Combined and sorted activities
  const allActivities = useMemo(() => {
    const combined = [
      ...checkInActivities,
      ...videoActivities,
      ...sessionActivities,
      ...dietActivities
    ];

    if (combined.length === 0) {
      return [
        {
          id: "default-1",
          category: "checkin",
          type: "Welcome to Divyyog",
          title: "Start Your Mindful Journey",
          subtitle: "Check in daily, watch practice videos, & join live classes",
          time: "Get Started",
          duration: "Daily Goal",
          accent: "#F29A0C",
          bg: "rgba(255,244,232,0.9)",
          border: "rgba(242,154,12,0.25)",
          ic: FiSun
        }
      ];
    }

    return combined;
  }, [checkInActivities, videoActivities, sessionActivities, dietActivities]);

  const filteredActivities = useMemo(() => {
    if (activeTab === "all") return allActivities;
    return allActivities.filter(a => a.category === activeTab);
  }, [allActivities, activeTab]);

  // Dynamic 7-Day Chart with Multi-Dimensional Practice + Diet + Check-in Data
  const weekActivity = useMemo(() => {
    const now = new Date();
    const currentDay = (now.getDay() + 6) % 7;
    const monday = new Date(now);
    monday.setDate(now.getDate() - currentDay);
    monday.setHours(0, 0, 0, 0);

    return dayNames.map((d, i) => {
      const dayDate = new Date(monday);
      dayDate.setDate(monday.getDate() + i);
      const isToday = (i === todayIdx);
      const isPast = (i < todayIdx);
      const isFuture = (i > todayIdx);

      // 1. Live Session Practice Minutes for this day
      let liveMins = 0;
      sessions.forEach(s => {
        if (s.isCheckedIn) {
          const classDate = parseUtcDate(s.scheduledAt);
          if (classDate.getFullYear() === dayDate.getFullYear() &&
              classDate.getMonth() === dayDate.getMonth() &&
              classDate.getDate() === dayDate.getDate()) {
            liveMins += (s.durationInMinutes || 45);
          }
        }
      });

      // 2. Video Practice Minutes for this day
      let videoMins = 0;
      if (isToday) {
        videoActivities.forEach(v => {
          videoMins += (v.mins || 0);
        });
      }

      // 3. Attendance / Check-In Record
      const hasAttendance = attendanceHistory.some(a => {
        const aDate = new Date(a.date || a.checkInDate || a.createdAt);
        return aDate.getFullYear() === dayDate.getFullYear() &&
               aDate.getMonth() === dayDate.getMonth() &&
               aDate.getDate() === dayDate.getDate();
      });

      const isCheckedInOnDay = (isToday && checkedIn) || hasAttendance;

      // 4. Diet Meal Logged on this day
      const plan = dietData?.plan || dietData;
      const completedMeals = dietData?.completedMeals || plan?.completedMeals || [];
      const hasDietOnDay = completedMeals.some(m => (m.day || "").toLowerCase().includes(d.full.toLowerCase())) || (isToday && dietActivities.length > 0);

      const totalMins = liveMins + videoMins + (isCheckedInOnDay ? 20 : 0);
      const hasActivity = totalMins > 0 || isCheckedInOnDay || hasDietOnDay;

      let barHeight = 12;
      if (hasActivity) {
        barHeight = Math.min(100, Math.max(38, Math.round((totalMins / 60) * 80) + 20));
      } else if (isToday) {
        barHeight = isCheckedInOnDay ? 65 : 28;
      }

      return {
        short: d.short,
        full: d.full,
        dateStr: dayDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        isToday,
        isPast,
        isFuture,
        hasActivity,
        liveMins,
        videoMins,
        totalMins,
        isCheckedInOnDay,
        hasDietOnDay,
        barHeight,
        status: hasActivity 
          ? `${totalMins > 0 ? `${totalMins}m Practice` : 'Active'} · ${isCheckedInOnDay ? 'Checked-In' : ''} ${hasDietOnDay ? '· Diet Tracked' : ''}`.trim()
          : isToday 
            ? (isCheckedInOnDay ? "Checked In Today" : "Today · Ready for Practice") 
            : isPast 
              ? "Rest & Recovery" 
              : "Upcoming Schedule"
      };
    });
  }, [dayNames, todayIdx, sessions, videoActivities, attendanceHistory, checkedIn, dietData, dietActivities]);

  // Aggregate stats for graphical metric capsules
  const totalWatchedVideos = videoActivities.length;
  const totalAttendedSessions = sessionActivities.length;
  const totalDietLogs = dietActivities.length;
  const currentStreak = stats?.streakDays || profile?.streak || 0;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] 2xl:grid-cols-[1fr_400px] gap-5 mb-5">
      <div className="rounded-[32px] p-6 md:p-7 relative overflow-hidden flex flex-col justify-between" style={glass}>
        
        {/* Header with Title & Live Legends */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-[18px] font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>Recent Activity</h3>
              <span className="px-2.5 py-0.5 rounded-full text-[9px] font-extrabold bg-[#F29A0C]/15 text-[#E97818]">
                Real-Time
              </span>
            </div>
            <p className="text-[12px] text-[#888] mt-0.5">Live practice, video watching, nutrition & daily check-ins</p>
          </div>

          <div className="flex items-center gap-3 text-[11px] text-[#777] font-semibold">
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm" style={{ background:"linear-gradient(135deg,#F29A0C,#E97818)" }}/> Today</div>
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm" style={{ background:"linear-gradient(135deg,#6B8A5A,#8BA47A)" }}/> Live</div>
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm" style={{ background:"linear-gradient(135deg,#8B5CF6,#7C3AED)" }}/> Video</div>
          </div>
        </div>

        {/* ── 4 Graphical Stat Capsules ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
          {/* 1. Practice Videos */}
          <div 
            onClick={() => setActiveTab(activeTab === "video" ? "all" : "video")}
            className="p-2.5 px-3 rounded-[16px] border transition-all cursor-pointer hover:scale-[1.02]"
            style={{ 
              background: activeTab === "video" ? "rgba(245,243,255,1)" : "rgba(245,243,255,0.65)",
              borderColor: activeTab === "video" ? "#8B5CF6" : "rgba(139,92,246,0.2)"
            }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-[#6D28D9]">Videos</span>
              <div className="w-5 h-5 rounded-full flex items-center justify-center bg-[#8B5CF6] text-white">
                <FiVideo size={9} />
              </div>
            </div>
            <p className="text-[15px] font-black text-[#1F1F1F]">
              {totalWatchedVideos} <span className="text-[10px] font-normal text-[#777]">watched</span>
            </p>
          </div>

          {/* 2. Live Sessions */}
          <div 
            onClick={() => setActiveTab(activeTab === "session" ? "all" : "session")}
            className="p-2.5 px-3 rounded-[16px] border transition-all cursor-pointer hover:scale-[1.02]"
            style={{ 
              background: activeTab === "session" ? "rgba(245,250,242,1)" : "rgba(245,250,242,0.65)",
              borderColor: activeTab === "session" ? "#6B8A5A" : "rgba(107,138,90,0.2)"
            }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-[#4E6845]">Sessions</span>
              <div className="w-5 h-5 rounded-full flex items-center justify-center bg-[#6B8A5A] text-white">
                <FiCheck size={9} />
              </div>
            </div>
            <p className="text-[15px] font-black text-[#1F1F1F]">
              {totalAttendedSessions} <span className="text-[10px] font-normal text-[#777]">attended</span>
            </p>
          </div>

          {/* 3. Diet Plan */}
          <div 
            onClick={() => setActiveTab(activeTab === "diet" ? "all" : "diet")}
            className="p-2.5 px-3 rounded-[16px] border transition-all cursor-pointer hover:scale-[1.02]"
            style={{ 
              background: activeTab === "diet" ? "rgba(236,253,245,1)" : "rgba(236,253,245,0.65)",
              borderColor: activeTab === "diet" ? "#10B981" : "rgba(16,185,129,0.2)"
            }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-[#065F46]">Diet</span>
              <div className="w-5 h-5 rounded-full flex items-center justify-center bg-[#10B981] text-white">
                <FiDroplet size={9} />
              </div>
            </div>
            <p className="text-[15px] font-black text-[#1F1F1F]">
              {totalDietLogs > 0 ? `${totalDietLogs}` : (dietData?.dietType || "Active")} <span className="text-[10px] font-normal text-[#777]">logged</span>
            </p>
          </div>

          {/* 4. Check-In Streak */}
          <div 
            onClick={() => setActiveTab(activeTab === "checkin" ? "all" : "checkin")}
            className="p-2.5 px-3 rounded-[16px] border transition-all cursor-pointer hover:scale-[1.02]"
            style={{ 
              background: activeTab === "checkin" ? "rgba(255,244,232,1)" : "rgba(255,244,232,0.65)",
              borderColor: activeTab === "checkin" ? "#F29A0C" : "rgba(242,154,12,0.25)"
            }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-[#B45309]">Streak</span>
              <div className="w-5 h-5 rounded-full flex items-center justify-center bg-[#F29A0C] text-white">
                <FiSun size={9} />
              </div>
            </div>
            <p className="text-[15px] font-black text-[#1F1F1F]">
              {currentStreak} <span className="text-[10px] font-normal text-[#777]">days</span>
            </p>
          </div>
        </div>

        {/* ── Dynamic Weekly Bar Chart ── */}
        <div className="relative mb-4 pt-1">
          <div className="flex items-end gap-2.5 h-24">
            {weekActivity.map((day, i) => {
              const isCurrentDay = day.isToday;
              const hasDonePractice = day.hasActivity;

              let barBg = "rgba(0,0,0,0.06)";
              let barShadow = "none";

              if (isCurrentDay) {
                barBg = "linear-gradient(180deg, #F29A0C, #E97818)";
                barShadow = "0 6px 18px rgba(242,154,12,0.42)";
              } else if (day.videoMins > 0 && day.liveMins === 0) {
                barBg = "linear-gradient(180deg, #8B5CF6, #7C3AED)";
                barShadow = "0 4px 12px rgba(139,92,246,0.28)";
              } else if (hasDonePractice) {
                barBg = "linear-gradient(180deg, #6B8A5A, #8BA47A)";
                barShadow = "0 4px 10px rgba(107,138,90,0.22)";
              } else if (day.isFuture) {
                barBg = "rgba(242,154,12,0.06)";
              }

              return (
                <div 
                  key={i} 
                  className="flex-1 flex flex-col items-center gap-1 relative group cursor-pointer"
                  onMouseEnter={() => setHoveredDay(day)}
                  onMouseLeave={() => setHoveredDay(null)}>
                  
                  {/* Tooltip on Hover */}
                  <div className="opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none absolute -top-12 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-xl bg-[#111111] text-white text-[10px] font-semibold whitespace-nowrap shadow-2xl z-30 flex flex-col items-center">
                    <span className="font-extrabold text-[#F29A0C]">{day.full} ({day.dateStr})</span>
                    <span className="text-[9px] text-zinc-300">{day.status}</span>
                  </div>

                  <motion.div 
                    className="w-full rounded-t-lg transition-all duration-300"
                    style={{ 
                      height: `${day.barHeight}%`, 
                      background: barBg, 
                      boxShadow: barShadow,
                      border: day.isFuture ? "1px dashed rgba(242,154,12,0.3)" : "none"
                    }}
                    initial={{ height: 0 }} 
                    whileInView={{ height: `${day.barHeight}%` }} 
                    viewport={{ once: true }} 
                    transition={{ duration: 0.8, delay: i * 0.04, ease: "easeOut" }} 
                  />

                  <div className="flex flex-col items-center">
                    <p className="text-[10px] font-extrabold transition-colors" 
                      style={{ 
                        color: isCurrentDay ? "#F29A0C" : hasDonePractice ? "#6B8A5A" : day.isFuture ? "#CCC" : "#888" 
                      }}>
                      {day.short}
                    </p>
                    {isCurrentDay && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#F29A0C] mt-0.5 animate-pulse" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Filter Pills Row ── */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 mb-3 scrollbar-none">
          {[
            { id: "all", label: "All" },
            { id: "video", label: `Videos (${videoActivities.length})` },
            { id: "session", label: `Live (${sessionActivities.length})` },
            { id: "diet", label: `Diet (${dietActivities.length})` },
            { id: "checkin", label: `Check-In (${checkInActivities.length})` },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="px-3 py-1.5 rounded-full text-[10px] font-bold transition-all whitespace-nowrap"
              style={{
                background: activeTab === tab.id ? "#1F1F1F" : "rgba(0,0,0,0.05)",
                color: activeTab === tab.id ? "#FFFFFF" : "#666666",
                boxShadow: activeTab === tab.id ? "0 3px 10px rgba(0,0,0,0.12)" : "none"
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Filtered Activity Feed ── */}
        <div className="flex flex-col gap-2.5 max-h-[225px] overflow-y-auto pr-1">
          {filteredActivities.map((item, idx) => {
            const isVideo = item.category === "video";
            return (
              <div 
                key={item.id || idx} 
                className="flex items-center gap-3 px-3.5 py-2.5 rounded-[16px] border transition-transform hover:scale-[1.01]" 
                style={{ 
                  background: item.bg,
                  borderColor: item.border || "transparent"
                }}>
                
                {/* Category Icon */}
                <div className="w-8 h-8 rounded-[11px] flex items-center justify-center flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${item.accent}, ${item.accent}CC)` }}>
                  <item.ic style={{ color: "#fff", fontSize: 14 }}/>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-[12px] font-extrabold text-[#1F1F1F] truncate">{item.title}</p>
                    <span className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider text-white"
                      style={{ background: item.accent }}>
                      {item.type}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#777] truncate">{item.subtitle}</p>

                  {/* Video Progress Mini Bar */}
                  {isVideo && item.progress !== undefined && (
                    <div className="mt-1 flex items-center gap-1.5 max-w-[150px]">
                      <div className="flex-1 h-1.5 rounded-full bg-[#8B5CF6]/20 overflow-hidden">
                        <div 
                          className="h-full rounded-full bg-[#8B5CF6] transition-all duration-300"
                          style={{ width: `${Math.min(100, item.progress)}%` }}
                        />
                      </div>
                      <span className="text-[9px] font-bold text-[#6D28D9]">{item.progress}%</span>
                    </div>
                  )}
                </div>

                {/* Duration Badge & Quick Play */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="px-2.5 py-1 rounded-full text-[9px] font-extrabold text-white shadow-sm whitespace-nowrap" 
                    style={{ background: item.accent }}>
                    {item.duration}
                  </span>

                  {isVideo && item.videoObj && onPlayVideo && (
                    <button
                      onClick={() => onPlayVideo(item.videoObj)}
                      className="w-7 h-7 rounded-full bg-[#8B5CF6] text-white flex items-center justify-center hover:scale-105 active:scale-95 transition-all shadow-sm"
                      title="Play Practice Video">
                      <FiPlay size={10} className="ml-0.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: Full-Section Ambient Rings Membership Pass Card */}
      {(() => {
        const activeSub = profile?.activeSubscription || profile?.subscriptions?.find(s => s.status === "Active" || s.status === "Approved") || profile?.subscriptions?.[0];
        const tierName = profile?.tier || activeSub?.packageName || activeSub?.tier || activeSub?.package?.name || "Free Trial";
        const userTierKey = normalizeTier(tierName);
        const isFree = !activeSub || userTierKey === "free" || tierName.toLowerCase().includes("free");
        const daysRemaining = daysLeft;
        const isSubActive = activeSub ? (activeSub.status === "Active" || activeSub.status === "Approved") && daysRemaining >= 0 : false;
        const tierPassName = isFree ? "Essential Wellness Pass" : `${tierName} Wellness Pass`;

        return (
          <motion.div initial={{ opacity:0, x:28 }} animate={{ opacity:1, x:0 }} transition={{ duration: 0.9 }}
            className="relative overflow-hidden rounded-[32px] p-6 md:p-7 text-white flex flex-col justify-between"
            style={isFree ? {
              background: "linear-gradient(150deg, #2D3D25 0%, #3D5533 50%, #1E2B18 100%)",
              boxShadow: "0 24px 60px rgba(30,43,24,0.45)",
              border: "1px solid rgba(255,255,255,0.12)"
            } : {
              background: "linear-gradient(150deg, #F89B29 0%, #E87417 50%, #C44E05 100%)",
              boxShadow: "0 24px 60px rgba(232,116,23,0.42)",
              border: "1px solid rgba(255,255,255,0.25)"
            }}>
            
            {/* ── Ambient Glow Backgrounds ── */}
            <div className="absolute top-0 right-0 w-56 h-56 rounded-full opacity-25 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(255,255,255,0.8), transparent 70%)", filter: "blur(50px)", transform: "translate(30%,-30%)" }} />
            <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-15 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(255,220,150,0.6), transparent 70%)", filter: "blur(45px)", transform: "translate(-30%,30%)" }} />
            <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
              style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 0)", backgroundSize: "16px 16px" }} />

            {/* ── Foreground Content ── */}
            <div className="relative z-10 flex flex-col justify-between h-full min-h-[380px]">
              
              {/* Header Row */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-200 animate-pulse" />
                    <p className="text-white/80 text-[10px] font-black tracking-[1.5px] uppercase">{tierPassName}</p>
                  </div>
                  <h2 className="text-[26px] font-black text-white leading-tight drop-shadow-sm" 
                    style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
                    {tierName} Yogi
                  </h2>
                </div>

                <div className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-[10px] font-black tracking-wider uppercase flex items-center gap-1.5 shadow-sm">
                  <span className={`w-2 h-2 rounded-full ${isSubActive ? "bg-emerald-300 shadow-[0_0_8px_#6ee7b7]" : "bg-amber-300"}`} />
                  {isSubActive ? "Active Pass" : isFree ? "Free Trial" : "Expired"}
                </div>
              </div>

              {/* Centerpiece Premium Lotus Medallion with Contained Orbit Rings */}
              <div className="my-2.5 flex flex-col items-center justify-center">
                <div className="relative w-[180px] h-[180px] flex items-center justify-center">
                  
                  {/* Outer Contained Orbit Ring (176px) */}
                  <motion.div 
                    animate={{ rotate: 360 }} 
                    transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
                    className="absolute w-[176px] h-[176px] rounded-full border border-white/20"
                    style={{ borderStyle: "dashed" }}>
                    <div className="absolute top-0 left-1/2 w-2.5 h-2.5 rounded-full bg-white -translate-x-1/2 -translate-y-1/2 shadow-[0_0_10px_#fff]" />
                    <div className="absolute bottom-0 left-1/2 w-1.5 h-1.5 rounded-full bg-white/70 -translate-x-1/2 translate-y-1/2" />
                  </motion.div>

                  {/* Mid Subtle Halo Ring (144px) */}
                  <motion.div 
                    animate={{ rotate: -360 }} 
                    transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
                    className="absolute w-[144px] h-[144px] rounded-full border border-white/25">
                    <div className="absolute top-1/2 right-0 w-2 h-2 rounded-full bg-amber-200 -translate-y-1/2 translate-x-1/2 shadow-[0_0_8px_#fde68a]" />
                  </motion.div>

                  {/* Inner Concentric Ring (116px) */}
                  <motion.div 
                    animate={{ rotate: 360 }} 
                    transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                    className="absolute w-[116px] h-[116px] rounded-full border border-white/20"
                    style={{ borderStyle: "dotted" }}>
                    <div className="absolute top-1/2 left-0 w-1.5 h-1.5 rounded-full bg-white/90 -translate-y-1/2 -translate-x-1/2" />
                  </motion.div>

                  {/* Center Radiant Pulse Light */}
                  <motion.div 
                    animate={{ scale: [0.9, 1.15, 0.9], opacity: [0.25, 0.5, 0.25] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute w-28 h-28 rounded-full bg-white/25 blur-lg pointer-events-none"
                  />

                  {/* Luminous frosted medallion (92px) */}
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    animate={{ y: [0, -3.5, 0] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                    className="w-[92px] h-[92px] rounded-full flex items-center justify-center relative shadow-[0_16px_38px_rgba(0,0,0,0.24)] cursor-pointer"
                    style={{ 
                      background: "linear-gradient(135deg, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.14) 100%)", 
                      backdropFilter: "blur(18px)", 
                      border: "2px solid rgba(255,255,255,0.6)",
                      boxShadow: "0 12px 32px rgba(0,0,0,0.18), inset 0 1px 2px rgba(255,255,255,0.9)"
                    }}>
                    <GiLotus size={40} className="text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.25)]" />
                  </motion.div>
                </div>
                <p className="text-[11.5px] font-extrabold text-white/95 mt-1 tracking-wide drop-shadow-sm flex items-center gap-1.5">
                  <FiSun size={13} className="text-amber-200" /> Sacred Practice Member
                </p>
              </div>

              {/* Modern Glassmorphic Status Box */}
              <div className="rounded-[22px] p-3.5 mb-4 backdrop-blur-xl border border-white/25 shadow-inner"
                style={{ background: "rgba(0, 0, 0, 0.14)" }}>
                {[
                  { label: "Membership Status", val: isSubActive ? "Active ✦" : isFree ? "Free Tier" : "Expired", icon: FiShield },
                  { label: "Wellness Score", val: `${wellnessScore} / 100`, icon: FiActivity },
                  { label: "Remaining Days", val: isSubActive ? `${daysRemaining} Days` : "0 Days", icon: FiCalendar }
                ].map((item, idx) => (
                  <div key={item.label} className={`flex items-center justify-between py-1.5 ${idx !== 2 ? 'border-b border-white/10' : ''}`}>
                    <div className="flex items-center gap-2">
                      <item.icon size={13} className="text-white/70" />
                      <span className="text-white/80 text-[11.5px] font-medium">{item.label}</span>
                    </div>
                    <span className="text-white text-[12px] font-extrabold tracking-wide drop-shadow-sm">{item.val}</span>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <motion.button whileHover={{ scale:1.02, y:-1 }} whileTap={{ scale:0.98 }}
                onClick={onManageMembership}
                className="w-full h-11 rounded-full font-black text-[12.5px] text-[#D85604] cursor-pointer shadow-[0_10px_25px_rgba(0,0,0,0.16)] hover:shadow-[0_14px_30px_rgba(0,0,0,0.22)] transition-all flex items-center justify-center gap-2"
                style={{ 
                  background: "linear-gradient(180deg, #FFFFFF 0%, #F9F9F9 100%)", 
                  fontFamily: "'Inter',sans-serif",
                  border: "1px solid rgba(255,255,255,0.8)" 
                }}>
                Manage Membership <FiArrowRight size={14} />
              </motion.button>
            </div>
          </motion.div>
        );
      })()}
    </div>
  );
}

/* ─── BOTTOM GRID ─────────────────────────────────────────────────── */
/* ─── BREATHE WITH ME — guided breathing studio (logs real mindful minutes) ─── */
const BREATH_PATTERNS = {
  box:     { name: "Box Breath",  tagline: "Focus & steady calm", phases: [["Inhale", 4], ["Hold", 4], ["Exhale", 4], ["Hold", 4]] },
  calm:    { name: "4-7-8 Calm",  tagline: "Unwind & let go",     phases: [["Inhale", 4], ["Hold", 7], ["Exhale", 8]] },
  balance: { name: "Coherent",    tagline: "Balance your energy", phases: [["Inhale", 5], ["Exhale", 5]] },
};
const ORB_MIN = 0.62;

function BreathingStudio({ onLogged }) {
  const [patternKey, setPatternKey] = useState("box");
  const [running, setRunning] = useState(false);
  const [breath, setBreath] = useState({ phaseIdx: 0, secsLeft: BREATH_PATTERNS.box.phases[0][1], cycles: 0 });
  const [elapsed, setElapsed] = useState(0);
  const [saving, setSaving] = useState(false);

  const pattern = BREATH_PATTERNS[patternKey];
  const { phaseIdx, secsLeft, cycles } = breath;
  const [phaseName, phaseLen] = pattern.phases[phaseIdx];
  const started = elapsed > 0;

  const reset = (key = patternKey) => {
    setRunning(false);
    setBreath({ phaseIdx: 0, secsLeft: BREATH_PATTERNS[key].phases[0][1], cycles: 0 });
    setElapsed(0);
  };

  // One tick per second: count down the phase, then advance to the next one (pure updates)
  useEffect(() => {
    if (!running) return;
    const phases = pattern.phases;
    const id = setInterval(() => {
      setElapsed(e => e + 1);
      setBreath(b => {
        if (b.secsLeft > 1) return { ...b, secsLeft: b.secsLeft - 1 };
        const next = (b.phaseIdx + 1) % phases.length;
        return { phaseIdx: next, secsLeft: phases[next][1], cycles: b.cycles + (next === 0 ? 1 : 0) };
      });
    }, 1000);
    return () => clearInterval(id);
  }, [running, pattern]);

  // Orb size follows the breath: grow on inhale, shrink on exhale, rest on hold
  const orbScale = (() => {
    if (!started && !running) return 0.8;
    if (phaseName === "Inhale") return 1;
    if (phaseName === "Exhale") return ORB_MIN;
    const prev = pattern.phases[(phaseIdx - 1 + pattern.phases.length) % pattern.phases.length][0];
    return prev === "Inhale" ? 1 : ORB_MIN;
  })();

  const finish = async () => {
    setRunning(false);
    const minutes = Math.floor(elapsed / 60);
    if (minutes < 1) {
      toast.info("Breathe for at least 1 minute to log your mindful time.");
      return;
    }
    setSaving(true);
    try {
      await userAPI.logMindfulness({ minutes, source: "Breathwork", activityName: pattern.name });
      toast.success(`🪷 ${minutes} mindful minute${minutes > 1 ? "s" : ""} logged. Beautiful practice!`);
      onLogged?.(minutes);
      reset();
    } catch {
      toast.error("Couldn't log your session. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const phaseColor = phaseName === "Inhale" ? T.orange : phaseName === "Exhale" ? T.olive : "#B08A4A";

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="relative rounded-[32px] p-6 overflow-hidden flex flex-col"
      style={{ background: "linear-gradient(160deg, rgba(255,250,242,0.95) 0%, rgba(245,250,242,0.92) 100%)", border: "1px solid rgba(242,154,12,0.18)", boxShadow: "0 20px 60px rgba(0,0,0,0.05)" }}>
      <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(242,154,12,0.18), transparent 70%)" }} />
      <div className="absolute -bottom-20 -left-16 w-48 h-48 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(107,138,90,0.16), transparent 70%)" }} />

      {/* Header */}
      <div className="relative flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-[18px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>Breathe With Me</h3>
          <p className="text-[11px] text-[#AAA] mt-0.5">A guided pause · logs your mindful minutes</p>
        </div>
        <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 shrink-0"
          style={{ background: "rgba(107,138,90,0.12)", color: T.olive, border: "1px solid rgba(107,138,90,0.25)" }}>
          <FiWind size={11} /> Guided
        </span>
      </div>

      {/* Pattern picker */}
      <div className="relative grid grid-cols-3 gap-1.5 p-1 rounded-2xl mb-5" style={{ background: "rgba(239,229,216,0.45)" }}>
        {Object.entries(BREATH_PATTERNS).map(([key, p]) => {
          const active = key === patternKey;
          return (
            <button key={key} type="button" disabled={started}
              onClick={() => { setPatternKey(key); reset(key); }}
              className="relative rounded-xl py-1.5 px-1 text-center border-0 cursor-pointer disabled:cursor-not-allowed"
              style={{ background: "transparent" }}>
              {active && (
                <motion.span layoutId="breathPatternPill" className="absolute inset-0 rounded-xl bg-white"
                  style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }} transition={{ type: "spring", stiffness: 420, damping: 34 }} />
              )}
              <span className="relative block text-[11px] font-black" style={{ color: active ? "#1F1F1F" : "#888", opacity: started && !active ? 0.45 : 1 }}>{p.name}</span>
            </button>
          );
        })}
      </div>

      {/* Breathing orb */}
      <div className="relative flex-1 flex items-center justify-center min-h-[190px] mb-2">
        {[1, 0.82, 0.64].map((s, i) => (
          <div key={i} className="absolute rounded-full" style={{ width: 176 * s, height: 176 * s, border: `1px dashed ${i === 0 ? "rgba(242,154,12,0.25)" : "rgba(107,138,90,0.2)"}` }} />
        ))}
        <motion.div
          animate={{ scale: orbScale }}
          transition={{ duration: running ? phaseLen : 0.6, ease: "easeInOut" }}
          className="relative w-[150px] h-[150px] rounded-full flex flex-col items-center justify-center text-white"
          style={{
            background: `radial-gradient(circle at 35% 30%, rgba(255,255,255,0.45), transparent 45%), linear-gradient(140deg, ${T.orange} 0%, ${T.orange2} 45%, ${T.olive} 100%)`,
            boxShadow: `0 18px 50px ${phaseColor}55, inset 0 2px 8px rgba(255,255,255,0.35)`,
          }}>
          <GiLotus size={26} className="opacity-90 mb-1" />
          <AnimatePresence mode="wait">
            <motion.span key={running || started ? `${phaseIdx}-${phaseName}` : "idle"}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }}
              className="text-[13px] font-black uppercase tracking-[2px]">
              {running || started ? phaseName : "Ready"}
            </motion.span>
          </AnimatePresence>
          <span className="text-[26px] font-black leading-none mt-1" style={{ fontFamily: "'Poppins',sans-serif" }}>
            {running || started ? secsLeft : pattern.phases.map(p => p[1]).join("·")}
          </span>
        </motion.div>
      </div>
      <p className="relative text-center text-[11px] font-semibold mb-4" style={{ color: "#999" }}>{pattern.tagline}</p>

      {/* Session stats */}
      <div className="relative grid grid-cols-2 gap-2.5 mb-4">
        <div className="rounded-2xl py-2.5 text-center" style={{ background: "rgba(255,255,255,0.75)", border: "1px solid rgba(239,229,216,0.8)" }}>
          <p className="text-[17px] font-black text-[#1F1F1F] leading-none" style={{ fontFamily: "'Poppins',sans-serif" }}>{mm}:{ss}</p>
          <p className="text-[9.5px] font-bold uppercase tracking-wider text-zinc-400 mt-1">Session Time</p>
        </div>
        <div className="rounded-2xl py-2.5 text-center" style={{ background: "rgba(255,255,255,0.75)", border: "1px solid rgba(239,229,216,0.8)" }}>
          <p className="text-[17px] font-black text-[#1F1F1F] leading-none" style={{ fontFamily: "'Poppins',sans-serif" }}>{cycles}</p>
          <p className="text-[9.5px] font-bold uppercase tracking-wider text-zinc-400 mt-1">Breath Cycles</p>
        </div>
      </div>

      {/* Controls */}
      <div className="relative flex gap-2.5">
        <motion.button type="button" whileTap={{ scale: 0.96 }} onClick={() => setRunning(r => !r)} disabled={saving}
          className="flex-1 h-11 rounded-full text-white text-[13px] font-bold flex items-center justify-center gap-2 border-0 cursor-pointer"
          style={{ background: running ? "linear-gradient(135deg,#1F1F1F,#3A3A3A)" : `linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow: running ? "0 8px 20px rgba(0,0,0,0.2)" : "0 8px 22px rgba(242,154,12,0.38)" }}>
          {running ? <><FiPause size={14} /> Pause</> : <><FiPlay size={14} /> {started ? "Resume" : "Start"}</>}
        </motion.button>
        {started && (
          <motion.button type="button" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} whileTap={{ scale: 0.96 }}
            onClick={finish} disabled={saving}
            className="h-11 px-4 rounded-full text-[12px] font-bold flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-60"
            style={{ background: "rgba(107,138,90,0.12)", color: T.olive, border: "1px solid rgba(107,138,90,0.3)" }}>
            <FiCheck size={13} /> {saving ? "Saving…" : "Finish"}
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

/* ─── NEXT MILESTONES — progress toward the next real goal on each ladder ─── */
const MILESTONE_LADDERS = [
  { key: "streak",   label: "Practice Streak", unit: "days",    icon: FiZap,      accent: T.orange2, steps: [3, 7, 14, 30, 60, 100] },
  { key: "sessions", label: "Sessions Done",   unit: "sessions", icon: FiActivity, accent: T.orange,  steps: [5, 10, 25, 50, 100, 200] },
  { key: "mindful",  label: "Mindful Minutes", unit: "mins",    icon: FiHeart,    accent: T.olive,   steps: [30, 60, 120, 300, 600, 1000] },
];

function MilestonePath({ values, className = "" }) {
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className={`rounded-[32px] p-6 ${className}`} style={glass}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[18px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>Your Next Milestones</h3>
          <p className="text-[11px] text-[#AAA] mt-0.5">How close you are to each next goal</p>
        </div>
        <div className="w-9 h-9 rounded-2xl flex items-center justify-center text-white shrink-0"
          style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow: "0 6px 18px rgba(242,154,12,0.35)" }}>
          <FiTarget size={16} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {MILESTONE_LADDERS.map((m, idx) => {
          const value = Math.max(0, Number(values[m.key]) || 0);
          const next = m.steps.find(s => s > value);
          const prev = [...m.steps].reverse().find(s => s <= value) || 0;
          const done = next === undefined;
          const pct = done ? 100 : Math.round(((value - prev) / (next - prev)) * 100);
          const reachedCount = m.steps.filter(s => s <= value).length;
          const Icon = m.icon;
          return (
            <div key={m.key} className="rounded-[20px] p-3.5 border" style={{ background: "rgba(255,255,255,0.65)", borderColor: `${m.accent}22` }}>
              <div className="flex items-center gap-3 mb-2.5">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0"
                  style={{ background: `linear-gradient(135deg,${m.accent},${m.accent}CC)`, boxShadow: `0 5px 14px ${m.accent}40` }}>
                  <Icon size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-[12.5px] font-bold text-[#1F1F1F] truncate">{m.label}</p>
                    <p className="text-[12px] font-black shrink-0" style={{ color: m.accent, fontFamily: "'Poppins',sans-serif" }}>
                      {value}{done ? "" : <span className="text-[#BBB] font-bold"> / {next}</span>}
                    </p>
                  </div>
                  {/* step dots */}
                  <div className="flex items-center gap-1 mt-1">
                    {m.steps.map(s => (
                      <span key={s} className="h-1 flex-1 rounded-full" style={{ background: s <= value ? m.accent : "rgba(239,229,216,0.9)" }} />
                    ))}
                  </div>
                </div>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(239,229,216,0.6)" }}>
                <motion.div className="h-full rounded-full" initial={{ width: 0 }} whileInView={{ width: `${pct}%` }} viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.1 * idx, ease: "easeOut" }}
                  style={{ background: `linear-gradient(90deg,${m.accent},${m.accent}AA)` }} />
              </div>
              <p className="text-[10.5px] font-semibold mt-1.5" style={{ color: "#999" }}>
                {done
                  ? `All ${m.steps.length} ${m.label.toLowerCase()} milestones reached. Incredible!`
                  : `${next - value} more ${m.unit} to reach ${next}${reachedCount ? ` · ${reachedCount} of ${m.steps.length} reached` : ""}`}
              </p>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

function BottomGrid({ sessions, liveProgress, attendanceHistory = [], profile, onBook, onCancel, onMindfulnessLogged }) {
  const { stats } = useCheckIn();
  const [hoveredDayId, setHoveredDayId] = useState(null);
  const [mindfulnessTotal, setMindfulnessTotal] = useState(0);

  useEffect(() => {
    const fetchMindfulness = async () => {
      try {
        const res = await userAPI.getMindfulness();
        if (res.data?.success) {
          setMindfulnessTotal(res.data.totalMinutes || 0);
        }
      } catch (e) {
        console.warn("Mindfulness fetch note:", e);
      }
    };
    fetchMindfulness();
  }, []);

  const now = useMemo(() => new Date(), []);
  const year = now.getFullYear();
  const month = now.getMonth();
  const monthName = now.toLocaleString("en-US", { month: "long" });

  const { calendarDays, attendedCount, attendanceRate } = useMemo(() => {
    const days = [];
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let attended = 0;

    const checkInMap = {};
    (attendanceHistory || []).forEach(a => {
      const dStr = new Date(a.attendanceDate).toDateString();
      checkInMap[dStr] = a;
    });

    const parseCity = (address) => {
      if (!address) return "Bangalore";
      const parts = address.split(',').map(p => p.trim());
      const knownCities = ["Bangalore", "Chennai", "Mumbai", "Delhi", "Kolkata", "Hyderabad", "Pune", "Ahmedabad", "Jaipur", "Gurgaon", "Noida", "Mysore", "Coimbatore"];
      for (const c of knownCities) {
        if (address.toLowerCase().includes(c.toLowerCase())) return c;
      }
      if (parts.length > 1) return parts[1];
      return parts[0];
    };

    const userCity = parseCity(profile?.address);

    const format12Hour = (timeStr) => {
      if (!timeStr) return "N/A";
      try {
        const [hourStr, minStr] = timeStr.split(":");
        const hour = parseInt(hourStr, 10);
        const ampm = hour >= 12 ? "PM" : "AM";
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minStr} ${ampm}`;
      } catch (e) {
        return timeStr;
      }
    };

    // Add padding days for the previous month
    for (let i = 0; i < startOffset; i++) {
      days.push({ isPadding: true });
    }

    // Add actual days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(year, month, day);
      const dStr = d.toDateString();
      const att = checkInMap[dStr];
      const isPresent = !!att;
      if (isPresent) attended += 1;

      days.push({
        isPadding: false,
        dayNum: day,
        isPresent,
        dateLabel: d.toLocaleDateString("en-US", { weekday: 'long', month: 'short', day: 'numeric' }),
        checkIn: att?.checkInTime ? format12Hour(att.checkInTime) : null,
        checkOut: att?.checkOutTime ? format12Hour(att.checkOutTime) : null,
        city: userCity
      });
    }

    const rate = Math.round((attended / daysInMonth) * 100);
    return { calendarDays: days, attendedCount: attended, attendanceRate: rate || 0 };
  }, [attendanceHistory, profile, year, month]);

  const workshops = useMemo(() => {
    if (!sessions) return [];
    return sessions
      .filter(s => s.isUpcoming)
      .slice(0, 3)
      .map(s => {
        const dateObj = parseUtcDate(s.scheduledAt);
        const day = dateObj.getDate().toString();
        const month = dateObj.toLocaleString("en-US", { month: "short" });
        return {
          id: s.id,
          day,
          month,
          title: s.title,
          sub: `Duration: ${s.durationInMinutes || 60} mins`,
          accent: s.tier === "Premium" ? "#E97818" : "#6B8A5A",
          tag: s.isCheckedIn ? "Booked" : "Open",
          session: s
        };
      });
  }, [sessions]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
      {/* Attendance Heatmap (raised layer so day tooltips render above the next card) */}
      <div className="relative z-20 rounded-[32px] p-6" style={glass}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-[18px] font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>Attendance</h3>
            <p className="text-[11px] text-[#AAA] mt-0.5">{monthName} {year} · <strong className="text-[#6B8A5A]">{attendedCount} attended</strong></p>
          </div>
          <span className="text-[20px] font-black text-[#6B8A5A]" style={{ fontFamily:"'Poppins',sans-serif" }}>{attendanceRate}%</span>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["M","T","W","T","F","S","S"].map((d,i) => <p key={i} className="text-center text-[10px] font-extrabold text-[#999]">{d}</p>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {calendarDays.map((day, i) => {
            if (day.isPadding) {
              return <div key={`pad-${i}`} className="aspect-square opacity-20 bg-transparent" />;
            }
            return (
              <div 
                key={`day-${day.dayNum}`}
                className="relative aspect-square"
                onMouseEnter={() => setHoveredDayId(day.dayNum)}
                onMouseLeave={() => setHoveredDayId(null)}
              >
                <motion.div
                  initial={{ opacity:0, scale:0.8 }} 
                  whileInView={{ opacity:1, scale:1 }} 
                  viewport={{ once:true }}
                  whileHover={{ scale:1.15, zIndex: 10 }}
                  className="w-full h-full rounded-[8px] flex items-center justify-center text-[11px] font-bold cursor-pointer relative"
                  style={day.isPresent 
                    ? { background:"linear-gradient(135deg,#6B8A5A,#8BA47A)", color:"#fff", boxShadow:"0 2px 8px rgba(107,138,90,0.35)" } 
                    : { background:"rgba(239,229,216,0.45)", color:"#777" }
                  }
                >
                  {day.dayNum}
                  {day.isPresent && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white" />
                  )}
                </motion.div>

                {/* Rich Custom Tooltip */}
                <AnimatePresence>
                  {hoveredDayId === day.dayNum && (
                    <motion.div
                      initial={{ opacity:0, scale:0.9, y:8 }}
                      animate={{ opacity:1, scale:1, y:0 }}
                      exit={{ opacity:0, scale:0.9, y:8 }}
                      transition={{ duration:0.18 }}
                      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 p-3.5 w-56 rounded-[20px] text-left text-[11px] z-50 pointer-events-none"
                      style={{ 
                        background:"rgba(255,255,255,0.98)",
                        border:"1px solid rgba(107,138,90,0.18)",
                        boxShadow:"0 12px 36px rgba(107,138,90,0.18)",
                        backdropFilter:"blur(12px)"
                      }}
                    >
                      <div className="font-extrabold text-[#1F1F1F] mb-2 border-b border-zinc-100 pb-1.5 flex justify-between items-center">
                        <span style={{ fontFamily:"'Poppins',sans-serif" }}>{day.dateLabel}</span>
                        <span className={`w-1.5 h-1.5 rounded-full ${day.isPresent ? 'bg-[#6B8A5A]' : 'bg-zinc-300'}`} />
                      </div>
                      {day.isPresent ? (
                        <div className="flex flex-col gap-1.5 font-semibold text-zinc-700">
                          <div className="flex items-center gap-1.5 text-[#6B8A5A] text-[10px] uppercase font-bold tracking-wider">
                            <FiCheck size={12} strokeWidth={3} />
                            <span>Checked In</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FiClock size={12} className="text-[#AAA]" />
                            <span>In: {day.checkIn || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FiClock size={12} className="text-[#AAA]" />
                            <span>Out: {day.checkOut || 'Active'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FiActivity size={12} className="text-[#AAA]" />
                            <span>Loc: {day.city}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-[#AAA] font-medium italic py-1">No attendance logged</div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 mt-4">
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm" style={{ background:"linear-gradient(135deg,#6B8A5A,#8BA47A)" }}/><span className="text-[10px] font-bold text-[#AAA]">Present</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm" style={{ background:"rgba(239,229,216,0.6)" }}/><span className="text-[10px] font-bold text-[#AAA]">Missed</span></div>
        </div>
        {attendanceHistory && attendanceHistory.length > 0 && (
          <div className="mt-4 pt-3 border-t border-[#EFE5D8]/50">
            <p className="text-[10px] text-[#AAA] uppercase font-bold tracking-wider mb-2">Recent Check-In Dates</p>
            <div className="flex flex-wrap gap-1.5">
              {attendanceHistory.slice(0, 3).map(a => {
                const dateStr = new Date(a.attendanceDate).toLocaleDateString("en-US", { weekday: 'short', month: 'short', day: 'numeric' });
                return (
                  <span key={a.id} className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-[#6B8A5A]/10 text-[#6B8A5A] border border-[#6B8A5A]/15">
                    {dateStr}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Events */}
      <div className="rounded-[32px] p-6 flex flex-col" style={glass}>
        <div className="flex justify-between items-center mb-5">
          <div>
            <h3 className="text-[18px] font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>Events</h3>
            <p className="text-[11px] text-[#AAA] mt-0.5">Upcoming workshops</p>
          </div>
          <FiCalendar style={{ color:"#F29A0C", fontSize:18 }}/>
        </div>
        <div className="flex-1 flex flex-col gap-3">
          {workshops.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-6 text-zinc-400 text-[12px] gap-2">
              <FiCalendar size={20} className="text-zinc-300" />
              No upcoming events scheduled.
            </div>
          ) : workshops.map((e) => (
            <div key={e.id} className="flex gap-3 items-center p-3 rounded-[18px]"
              style={{ background:`${e.accent}0D`, border:`1px solid ${e.accent}18` }}>
              <div className="w-11 h-11 rounded-[14px] flex flex-col items-center justify-center flex-shrink-0"
                style={{ background:`linear-gradient(135deg,${e.accent},${e.accent}CC)`, boxShadow:`0 6px 14px ${e.accent}40` }}>
                <span className="text-white text-[13px] font-black leading-none">{e.day}</span>
                <span className="text-white/80 text-[8px] font-bold uppercase">{e.month}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold text-[#1F1F1F] truncate">{e.title}</p>
                <p className="text-[11px] text-[#AAA]">{e.sub}</p>
              </div>
              {e.tag === "Booked" ? (
                <button onClick={() => onCancel(e.id)} className="px-3 py-1 rounded-full text-[10px] font-bold flex-shrink-0 bg-orange-500/10 text-orange-600 border border-orange-500/20 active:scale-95 cursor-pointer">
                  Cancel
                </button>
              ) : (
                <button onClick={() => onBook(e.id)} className="px-3 py-1 rounded-full text-[10px] font-bold flex-shrink-0 bg-[#E97818]/10 text-[#E97818] border border-[#E97818]/20 active:scale-95 cursor-pointer">
                  Book
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Column 3: Guided breathing */}
      <BreathingStudio
        onLogged={(mins) => {
          setMindfulnessTotal(t => t + mins);
          onMindfulnessLogged?.(mins);
        }}
      />

      {/* Row 2: Next milestones as a full-width strip */}
      <MilestonePath
        className="md:col-span-3"
        values={{
          streak: stats?.streakDays || 0,
          sessions: stats?.totalPractices || attendanceHistory?.length || 0,
          mindful: mindfulnessTotal || 0,
        }}
      />
    </div>
  );
}

/* ─── MEMBERSHIP BANNER ───────────────────────────────────────────── */
function MembershipBanner({ profile, daysLeft, onManage }) {
  const activeSub = profile?.activeSubscription || profile?.subscriptions?.find(s => s.status === "Active" || s.status === "Approved") || profile?.subscriptions?.[0];
  const userTier = normalizeTier(profile?.tier);
  const isFree = !activeSub || userTier === "free" || (profile?.tier || "").toLowerCase().includes("free");

  const expireDate = activeSub?.endDate
    ? new Date(activeSub.endDate).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : "No Active Subscription";

  const tierTitle = profile?.tier || activeSub?.packageName || activeSub?.package?.name || "Free Yogi";

  return (
    <motion.div initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
      className="relative overflow-hidden rounded-[32px] p-8 flex flex-col md:flex-row items-start md:items-center justify-between mb-5 gap-6"
      style={isFree ? {
        background: "linear-gradient(145deg, #2D3A26, #4E6845, #3E5C2E)",
        boxShadow: "0 24px 60px rgba(45,58,38,0.35)"
      } : {
        background: "linear-gradient(145deg, #6B8A5A, #8BA47A, #4E6845)",
        boxShadow: "0 24px 60px rgba(107,138,90,0.4)"
      }}>
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{ backgroundImage:"radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize:"20px 20px" }} />
      <div className="absolute right-0 top-0 w-64 h-64 rounded-full opacity-10"
        style={{ background:"radial-gradient(circle,#fff,transparent)", filter:"blur(40px)", transform:"translate(30%,-30%)" }} />

      <div className="relative max-w-xl">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider text-white/90"
            style={{ background: "rgba(255,255,255,0.18)" }}>
            {isFree ? "Essential Pass" : `${tierTitle} Pass`}
          </span>
          <span className="text-white/60 text-[11px] font-medium">
            {isFree ? "Free Access Tier" : `Active until ${expireDate}`}
          </span>
        </div>
        <h2 className="text-[26px] font-black text-white mb-2" style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.5px" }}>
          {isFree ? "Upgrade to Divyyog Premium Membership" : `${tierTitle} Yogi · ${daysLeft} Days Remaining`}
        </h2>
        <p className="text-white/80 text-[13px] leading-relaxed">
          {isFree
            ? "Unlock exclusive 1-on-1 live masterclasses, personalized Ayurvedic diet schedules, and unlimited replay access across all yoga styles."
            : "Enjoy full access to daily live sessions, premium video library, and personalized nutrition tracking. Keep your wellness momentum going!"}
        </p>
      </div>

      <div className="flex items-center gap-4 flex-shrink-0 relative w-full md:w-auto justify-between md:justify-end">
        <div className="text-center rounded-[20px] px-6 py-4" style={{ background:"rgba(255,255,255,0.12)" }}>
          <p className="text-[30px] font-black text-white leading-none" style={{ fontFamily:"'Poppins',sans-serif" }}>
            {isFree ? 0 : daysLeft}
          </p>
          <p className="text-white/65 text-[11px] font-bold mt-1">Days Remaining</p>
        </div>
        <button onClick={onManage} className="h-12 px-7 rounded-full font-bold text-[13px] text-[#2D3A26] flex items-center gap-2 cursor-pointer transition-transform active:scale-95"
          style={{ background:"rgba(255,255,255,0.95)", fontFamily:"'Inter',sans-serif", boxShadow:"0 8px 20px rgba(0,0,0,0.12)", whiteSpace:"nowrap" }}>
          {isFree ? "Upgrade Membership" : "Manage Membership"} <FiArrowRight size={13}/>
        </button>
      </div>
    </motion.div>
  );
}

/* ─── MAIN EXPORT ─────────────────────────────────────────────────── */
export default function Dashboard() {
  const navigate = useNavigate();
  const { checkedIn } = useCheckIn();
  const { packages: dynamicPackages = [] } = useDynamicPackages();
  const [upgradeModalSession, setUpgradeModalSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [videos, setVideos] = useState([]);
  const [liveProgress, setLiveProgress] = useState(null);
  const [videoProgressMap, setVideoProgressMap] = useState({});
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [mindfulnessTotal, setMindfulnessTotal] = useState(0);
  const [achievementSummary, setAchievementSummary] = useState(null);
  const [userGoals, setUserGoals] = useState([]);
  const [dietData, setDietData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeVideo, setActiveVideo] = useState(null);
  const lastSyncedPctRef = useRef({});

  const allowedVideos = useMemo(() => {
    if (!videos || !profile) return [];
    const userRank = profile?.accessLevel ?? profile?.userRank ?? getTierRank(profile?.tier, dynamicPackages);
    return videos.filter(v => canUserAccessContent(v.tier, profile.tier, userRank, dynamicPackages));
  }, [videos, profile, dynamicPackages]);

  const activeSessions = useMemo(() => {
    if (!sessions || !Array.isArray(sessions)) return [];
    return sessions.filter(s => s && (s.isLive || s.isUpcoming));
  }, [sessions]);

  const fetchDashboardData = async () => {
    try {
      const [profileRes, sessionsRes, videosRes, progressRes, videoProgressRes, attendanceRes, mindfulnessRes, achievementRes, goalsRes, dietRes] = await Promise.all([
        userAPI.getProfile().catch(() => ({ data: null })),
        liveSessionAPI.getSessions().catch(() => ({ data: null })),
        practiceVideoAPI.getVideos().catch(() => ({ data: null })),
        liveSessionAPI.getProgress().catch(() => ({ data: null })),
        practiceVideoAPI.getProgress().catch(() => ({ data: null })),
        userAPI.getAttendanceHistory().catch(() => ({ data: null })),
        userAPI.getMindfulness().catch(() => ({ data: null })),
        achievementAPI.getSummary().catch(() => ({ data: null })),
        goalsAPI.getGoals().catch(() => ({ data: null })),
        dietPlanAPI.getMyPlan().catch(() => ({ data: null }))
      ]);

      if (profileRes.data?.success) {
        setProfile(profileRes.data.data);
      }
      if (sessionsRes.data?.success) {
        setSessions(sessionsRes.data.data);
      }
      if (videosRes.data?.success) {
        setVideos(videosRes.data.data);
      }
      if (progressRes.data?.success) {
        setLiveProgress(progressRes.data);
      }
      if (videoProgressRes?.data) {
        const rawMap = (videoProgressRes.data?.success ? videoProgressRes.data.data : videoProgressRes.data) || {};
        const normalizedMap = {};
        Object.keys(rawMap).forEach(key => {
          const rawVal = rawMap[key];
          const k = key.toString();
          if (typeof rawVal === "number") {
            normalizedMap[k] = rawVal;
          } else if (rawVal && typeof rawVal === "object") {
            normalizedMap[k] = rawVal.progress ?? rawVal.Progress ?? 0;
          }
        });
        setVideoProgressMap(normalizedMap);
      }
      if (attendanceRes.data?.success) {
        setAttendanceHistory(attendanceRes.data.data || []);
      }
      if (mindfulnessRes?.data) {
        const mTotal = mindfulnessRes.data.totalMinutes ?? mindfulnessRes.data.TotalMinutes ?? mindfulnessRes.data.data?.totalMinutes ?? 0;
        setMindfulnessTotal(mTotal);
      }
      if (achievementRes?.data?.success) {
        setAchievementSummary(achievementRes.data.data);
      }
      if (goalsRes?.data?.success) {
        setUserGoals(goalsRes.data.data || []);
      }
      if (dietRes?.data?.success) {
        setDietData(dietRes.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Auto-refresh when tab is focused
    const handleFocus = () => fetchDashboardData();
    window.addEventListener("focus", handleFocus);
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchDashboardData();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    // Real-time SignalR sync — same API base as Live Sessions / api.js
    const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5154";
    const token = localStorage.getItem("divyayoga_token") || localStorage.getItem("token") || localStorage.getItem("jwt");
    const connection = new HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}/hubs/livesession`, {
        accessTokenFactory: () => token
      })
      .withAutomaticReconnect()
      .build();

    connection.start().then(() => {
      connection.on("SessionUpdated", () => fetchDashboardData());
      connection.on("SubscriptionUpdated", () => fetchDashboardData());
      connection.on("ProfileUpdated", () => fetchDashboardData());
    }).catch(() => null);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
      connection.stop().catch(() => null);
    };
  }, [checkedIn]);

  const daysLeft = useMemo(() => {
    const activeSub = profile?.activeSubscription || profile?.subscriptions?.find(s => s.status === "Active" || s.status === "Approved") || profile?.subscriptions?.[0];
    if (!activeSub) return 0;
    if (activeSub.daysLeft !== undefined && activeSub.daysLeft !== null) {
      return Math.max(0, activeSub.daysLeft);
    }
    if (activeSub.endDate) {
      const diff = new Date(activeSub.endDate) - new Date();
      return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    }
    return 0;
  }, [profile]);

  const handleBookSlot = async (id) => {
    try {
      const res = await liveSessionAPI.checkIn(id);
      if (res.data?.success) {
        setSessions(prev => prev.map(s => s.id === id ? { ...s, isCheckedIn: true, enrolledCount: (s.enrolledCount || 0) + 1 } : s));
        toast.success("Slot booked successfully!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to book slot");
    }
  };

  const handleCancelBooking = async (id) => {
    try {
      const res = await liveSessionAPI.cancelBooking(id);
      if (res.data?.success) {
        setSessions(prev => prev.map(s => s.id === id ? { ...s, isCheckedIn: false, enrolledCount: Math.max(0, (s.enrolledCount || 1) - 1) } : s));
        toast.success("Booking cancelled successfully.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to cancel booking");
    }
  };

  const handleContinuePractice = () => {
    const paused = allowedVideos.find(v => {
      const prog = videoProgressMap[v.id.toString()];
      return prog && prog > 0 && prog < 100;
    });
    const videoToPlay = paused || allowedVideos[0];
    if (videoToPlay) {
      handlePlayVideo(videoToPlay);
    } else {
      navigate("/user/traininglibrary");
    }
  };

  const handlePlayVideo = (video) => {
    setActiveVideo(video);
  };

  const handleVideoUpdate = async (videoId, updates) => {
    if (updates.pct !== undefined) {
      const vidKey = videoId.toString();
      const newPct = Math.round(updates.pct);
      setVideoProgressMap(prev => ({
        ...prev,
        [vidKey]: newPct
      }));
      const lastSynced = lastSyncedPctRef.current[videoId] || 0;
      if (Math.abs(newPct - lastSynced) >= 5 || newPct === 100) {
        lastSyncedPctRef.current[videoId] = newPct;
        try {
          await practiceVideoAPI.updateProgress({ videoId: Number(videoId), progress: newPct });
        } catch (err) {
          console.error("Failed to sync video progress to server:", err);
        }
      }
    }
  };

  if (loading) {
    return (
      <UserDashboardLayout>
        <BrandLoader 
          message="Loading Wellness Dashboard..." 
          subMessage="Preparing your yoga statistics and active journey" 
        />
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="w-full min-w-0" style={{ fontFamily:"'Inter', sans-serif" }}>
        <link rel="preconnect" href="https://fonts.googleapis.com"/>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet"/>
        <div className="fixed top-0 right-0 pointer-events-none" style={{ width:700, height:700, background:"radial-gradient(circle,rgba(242,154,12,0.08),transparent 70%)", filter:"blur(80px)", transform:"translate(35%,-35%)" }}/>
        <div className="fixed bottom-0 left-0 pointer-events-none" style={{ width:600, height:600, background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)", filter:"blur(80px)", transform:"translate(-35%,35%)" }}/>
        <Particles/>
        <section className="relative z-10 w-full min-w-0 py-2">
          <Topbar/>
          <HeroSection 
            profile={profile} 
            liveProgress={liveProgress} 
            sessions={sessions} 
            daysLeft={daysLeft}
            videos={allowedVideos}
            videoProgressMap={videoProgressMap}
            attendanceHistory={attendanceHistory}
            onContinuePractice={handleContinuePractice}
            onExploreClasses={() => navigate("/user/livesession")}
            onExploreMeditation={() => navigate("/user/traininglibrary")}
            onManageMembership={() => navigate("/user/profile?tab=subscription")}
            onBookSession={handleBookSlot}
            onCancelBooking={handleCancelBooking}
            onJoinSession={(id) => navigate(`/live-room/${id}`)}
            onUpgradeSession={(sess) => setUpgradeModalSession(sess)}
            userTier={profile?.tier || "Free"}
            userRank={profile?.accessLevel ?? profile?.userRank ?? getTierRank(profile?.tier, dynamicPackages)}
            packages={dynamicPackages}
          />
          <StatsBand 
            profile={profile} 
            mindfulnessTotal={mindfulnessTotal}
            achievementSummary={achievementSummary}
            userGoals={userGoals}
            dietData={dietData}
          />
          {activeSessions.length > 1 && (
            <div id="live-sessions-section">
              <UpcomingSessions 
                sessions={sessions} 
                userTier={profile?.tier || "Free"}
                userRank={profile?.accessLevel ?? profile?.userRank ?? getTierRank(profile?.tier, dynamicPackages)}
                packages={dynamicPackages}
                onBook={handleBookSlot} 
                onCancel={handleCancelBooking}
                onJoin={(id) => navigate(`/live-room/${id}`)}
                onUpgrade={(sess) => setUpgradeModalSession(sess)}
                onExplore={() => navigate("/user/livesession")}
              />
            </div>
          )}
          <div id="practice-section">
            <TodayPractice 
              videos={allowedVideos} 
              videoProgressMap={videoProgressMap} 
              onPlayVideo={handlePlayVideo}
              sessions={sessions}
              attendanceHistory={attendanceHistory}
              liveProgress={liveProgress}
              userGoals={userGoals}
            />
          </div>
          <div id="achievements-section">
            <Achievements profile={profile} />
          </div>
          <ActivityAndWisdom 
            sessions={sessions}
            profile={profile}
            daysLeft={daysLeft}
            attendanceHistory={attendanceHistory}
            videos={allowedVideos}
            videoProgressMap={videoProgressMap}
            dietData={dietData}
            onPlayVideo={handlePlayVideo}
            onManageMembership={() => navigate("/user/profile?tab=subscription")}
          />
          <div id="goals-section">
            <BottomGrid 
              sessions={sessions} 
              liveProgress={liveProgress}
              attendanceHistory={attendanceHistory}
              profile={profile}
              onBook={handleBookSlot}
              onCancel={handleCancelBooking}
              onMindfulnessLogged={(mins) => setMindfulnessTotal(t => t + mins)}
            />
          </div>
          <MembershipBanner
            profile={profile} 
            daysLeft={daysLeft}
            onManage={() => navigate("/user/profile?tab=subscription")}
          />
          <AnimatePresence>
            {upgradeModalSession && (
              <UpgradeModal 
                session={upgradeModalSession} 
                onClose={() => setUpgradeModalSession(null)} 
                packages={dynamicPackages} 
              />
            )}
            {activeVideo && (
              <PlayerModal 
                video={activeVideo} 
                progressPercent={videoProgressMap[activeVideo.id.toString()] || 0} 
                onVideoUpdate={handleVideoUpdate} 
                onClose={() => setActiveVideo(null)} 
              />
            )}
          </AnimatePresence>
        </section>
      </div>
    </UserDashboardLayout>
  );
}